import React, { useState, useEffect } from 'react';
import { MerchantsTable, CategoriesTable, ItemsTable, MissionsTable, AreasTable, UsersTable } from './SoukClothesAdminComponents';
import { EditModal, AddModal, DeleteConfirm, AddCoinsModal, EditCoinsModal, LockConfirmModal } from './SoukClothesAdminModals';
import { AnalyticsTab } from '../GamificationTabs';
import { DressUpAdminInfo } from './DressUpAdminInfo';
import IconPicker from './IconPicker';

const SoukClothesAdmin = ({ API, adminToken, showNotification, isDarkMode }) => {
  const [gameTab, setGameTab] = useState('free-market'); // 'free-market' | 'missions'
  const [activeSection, setActiveSection] = useState('fm-settings');
  const [loading, setLoading] = useState(false);
  
  // Data states
  const [settings, setSettings] = useState(null);
  const [merchants, setMerchants] = useState([]);
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [dressUpItems, setDressUpItems] = useState([]);
  const [missions, setMissions] = useState([]);
  const [users, setUsers] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [areas, setAreas] = useState([]);
  const [avatars, setAvatars] = useState([]);
  const [tradingData, setTradingData] = useState(null);
  const [bannedWords, setBannedWords] = useState([]);
  const [newBannedWord, setNewBannedWord] = useState('');
  const [flaggedMessages, setFlaggedMessages] = useState([]);
  const [darijaPhrases, setDarijaPhrases] = useState([]);
  const [newPhrase, setNewPhrase] = useState({ darija: '', arabic: '', meaning: '' });
  const [adminVocab, setAdminVocab] = useState([]);
  const [vocabTab, setVocabTab] = useState('marketplace');
  const [newVocab, setNewVocab] = useState({ word: '', meaning: '', arabic: '', category: 'phrase' });

  // Modal states
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showAddCoinsModal, setShowAddCoinsModal] = useState(false);
  const [showEditCoinsModal, setShowEditCoinsModal] = useState(false);
  const [showLockConfirmModal, setShowLockConfirmModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  

  // Fetch data on mount and section change
  useEffect(() => {
    fetchData();
  }, [activeSection]);

  const fetchData = async () => {
    setLoading(true);
    try {
      switch(activeSection) {
        case 'fm-settings':
        case 'settings':
        case 'marketplace':
          const settingsRes = await fetch(`${API}/admin/souk-clothes/settings`, {
            headers: { 'Authorization': `Bearer ${adminToken}` }
          });
          const sData = await settingsRes.json();
          setSettings(sData);
          if (activeSection === 'marketplace') {
            setDarijaPhrases(Array.isArray(sData.darija_phrases) && sData.darija_phrases.length > 0
              ? sData.darija_phrases
              : [
                  { darija: 'Tbadel m3aya?', arabic: 'تبادل معايا؟', meaning: 'Wanna trade with me?' },
                  { darija: 'Bssaha!', arabic: 'بالصحة!', meaning: 'Wear it in good health!' },
                  { darija: 'Chhal hada?', arabic: 'شحال هذا؟', meaning: 'How much is this?' },
                  { darija: 'Safi, mttaf9in!', arabic: 'صافي، متفاقين!', meaning: 'Deal, we agree!' },
                  { darija: 'Hadi 7aja zwina!', arabic: 'هادي حاجة زوينة!', meaning: 'This is a nice thing!' },
                  { darija: 'Allah y3tik saha!', arabic: 'الله يعطيك الصحة!', meaning: 'God give you health!' },
                  { darija: 'Zwin bezzaf!', arabic: 'زوين بزاف!', meaning: 'Very beautiful!' },
                  { darija: 'Rkhis!', arabic: 'رخيص!', meaning: 'Cheap!' },
                  { darija: 'Shukran bzaf!', arabic: 'شكرا بزاف!', meaning: 'Thank you very much!' },
                  { darija: 'Ghali bezzaf!', arabic: 'غالي بزاف!', meaning: 'Too expensive!' },
                ]);
          }
          break;
        case 'vocab':
          const [vocabRes, vocabSettingsRes] = await Promise.all([
            fetch(`${API}/admin/souk-clothes/vocab-words`, { headers: { 'Authorization': `Bearer ${adminToken}` } }),
            fetch(`${API}/admin/souk-clothes/settings`, { headers: { 'Authorization': `Bearer ${adminToken}` } }),
          ]);
          if (vocabRes.ok) {
            const vData = await vocabRes.json();
            setAdminVocab(Array.isArray(vData) ? vData : []);
          }
          if (vocabSettingsRes.ok) {
            setSettings(await vocabSettingsRes.json());
          }
          break;
        case 'merchants':
          const merchantsRes = await fetch(`${API}/admin/souk-clothes/merchants`, {
            headers: { 'Authorization': `Bearer ${adminToken}` }
          });
          setMerchants(await merchantsRes.json());
          // Also fetch areas for the area_id dropdown
          const areasForMerchants = await fetch(`${API}/admin/souk-clothes/areas`, {
            headers: { 'Authorization': `Bearer ${adminToken}` }
          });
          if (areasForMerchants.ok) {
            const ad = await areasForMerchants.json();
            setAreas(Array.isArray(ad) ? ad : []);
          }
          break;
        case 'categories':
          const categoriesRes = await fetch(`${API}/admin/souk-clothes/categories`, {
            headers: { 'Authorization': `Bearer ${adminToken}` }
          });
          if (categoriesRes.ok) {
            setCategories(await categoriesRes.json());
          } else {
            setCategories([]);
          }
          break;
        case 'items':
          const itemsRes = await fetch(`${API}/admin/souk-clothes/items`, {
            headers: { 'Authorization': `Bearer ${adminToken}` }
          });
          const merchantsForItems = await fetch(`${API}/admin/souk-clothes/merchants`, {
            headers: { 'Authorization': `Bearer ${adminToken}` }
          });
          const categoriesForItems = await fetch(`${API}/admin/souk-clothes/categories`, {
            headers: { 'Authorization': `Bearer ${adminToken}` }
          });
          setItems(await itemsRes.json());
          setMerchants(await merchantsForItems.json());
          if (categoriesForItems.ok) {
            setCategories(await categoriesForItems.json());
          }
          break;
        case 'dressup':
          const dressUpRes = await fetch(`${API}/admin/souk-clothes/dressup-items`, {
            headers: { 'Authorization': `Bearer ${adminToken}` }
          });
          if (dressUpRes.ok) {
            const data = await dressUpRes.json();
            setDressUpItems(Array.isArray(data) ? data : []);
          } else {
            setDressUpItems([]);
          }
          break;
        case 'missions':
          const missionsRes = await fetch(`${API}/admin/souk-clothes/missions`, {
            headers: { 'Authorization': `Bearer ${adminToken}` }
          });
          setMissions(await missionsRes.json());
          // Also fetch items for the required_items editor
          const itemsForMissions = await fetch(`${API}/admin/souk-clothes/items`, {
            headers: { 'Authorization': `Bearer ${adminToken}` }
          });
          if (itemsForMissions.ok) setItems(await itemsForMissions.json());
          break;
        case 'areas':
          const areasRes = await fetch(`${API}/admin/souk-clothes/areas`, {
            headers: { 'Authorization': `Bearer ${adminToken}` }
          });
          if (areasRes.ok) {
            const areasData = await areasRes.json();
            setAreas(Array.isArray(areasData) ? areasData : []);
          } else {
            setAreas([]);
          }
          break;
        case 'users':
          const usersRes = await fetch(`${API}/admin/souk-clothes/users`, {
            headers: { 'Authorization': `Bearer ${adminToken}` }
          });
          if (usersRes.ok) {
            const usersData = await usersRes.json();
            console.log('✅ Users fetched:', usersData.length, 'users');
            setUsers(Array.isArray(usersData) ? usersData : []);
          } else {
            console.error('❌ Failed to fetch users:', usersRes.status);
            setUsers([]);
          }
          break;
        case 'analytics':
          const analyticsRes = await fetch(`${API}/admin/souk-clothes/analytics`, {
            headers: { 'Authorization': `Bearer ${adminToken}` }
          });
          if (analyticsRes.ok) {
            setAnalytics(await analyticsRes.json());
          } else {
            setAnalytics(null);
          }
          break;
        case 'avatars':
          const avatarsRes = await fetch(`${API}/admin/souk-clothes/avatars`, {
            headers: { 'Authorization': `Bearer ${adminToken}` }
          });
          if (avatarsRes.ok) {
            const avatarData = await avatarsRes.json();
            setAvatars(Array.isArray(avatarData) ? avatarData : []);
          } else {
            setAvatars([]);
          }
          break;
        case 'trading':
          const tradingRes = await fetch(`${API}/admin/souk-clothes/trading`, {
            headers: { 'Authorization': `Bearer ${adminToken}` }
          });
          if (tradingRes.ok) {
            setTradingData(await tradingRes.json());
          } else {
            setTradingData(null);
          }
          // Fetch banned words
          try {
            const bwRes = await fetch(`${API}/admin/souk-clothes/trading/banned-words`, {
              headers: { 'Authorization': `Bearer ${adminToken}` }
            });
            if (bwRes.ok) {
              const bwData = await bwRes.json();
              setBannedWords(bwData.banned_words || []);
            }
          } catch {}
          // Fetch flagged messages
          try {
            const fmRes = await fetch(`${API}/admin/souk-clothes/trading/flagged-messages`, {
              headers: { 'Authorization': `Bearer ${adminToken}` }
            });
            if (fmRes.ok) {
              setFlaggedMessages(await fmRes.json());
            }
          } catch {}
          break;
      }
    } catch (error) {
      showNotification('Error fetching data: ' + error.message, 'error');
    }
    setLoading(false);
  };

  const handleSave = async (data, endpoint, method = 'PUT') => {
    try {
      const res = await fetch(`${API}${endpoint}`, {
        method,
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
        body: JSON.stringify(data)
      });
      
      // Check if response is JSON
      const contentType = res.headers.get('content-type');
      let result;
      if (contentType && contentType.includes('application/json')) {
        result = await res.json();
      } else {
        // Handle text response
        const text = await res.text();
        result = { success: res.ok, message: text };
      }
      
      if (result.success || res.ok) {
        showNotification('Saved successfully!', 'success');
        fetchData();
        setShowEditModal(false);
        setShowAddModal(false);
      } else {
        showNotification('Error: ' + (result.message || 'Unknown error'), 'error');
      }
    } catch (error) {
      showNotification('Error saving: ' + error.message, 'error');
    }
  };

  const handleAddCoins = async (data) => {
    try {
      const res = await fetch(`${API}/admin/souk-clothes/add-coins`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
        body: JSON.stringify(data)
      });
      
      const contentType = res.headers.get('content-type');
      let result;
      if (contentType && contentType.includes('application/json')) {
        result = await res.json();
      } else {
        result = { success: res.ok };
      }
      
      if (result.success || res.ok) {
        showNotification(`Successfully added ${data.amount} coins!`, 'success');
        setShowAddCoinsModal(false);
        fetchData(); // Refresh user list
      } else {
        showNotification('Error adding coins: ' + (result.message || 'Unknown error'), 'error');
      }
    } catch (error) {
      showNotification('Error adding coins: ' + error.message, 'error');
    }
  };

  const handleEditCoins = async (data) => {
    try {
      const res = await fetch(`${API}/admin/souk-clothes/set-coins`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
        body: JSON.stringify(data)
      });
      
      const contentType = res.headers.get('content-type');
      let result;
      if (contentType && contentType.includes('application/json')) {
        result = await res.json();
      } else {
        result = { success: res.ok };
      }
      
      if (result.success || res.ok) {
        showNotification(`Successfully set coins to ${data.new_amount}!`, 'success');
        setShowEditCoinsModal(false);
        fetchData(); // Refresh user list
      } else {
        showNotification('Error setting coins: ' + (result.message || 'Unknown error'), 'error');
      }
    } catch (error) {
      showNotification('Error setting coins: ' + error.message, 'error');
    }
  };

  const handleToggleWallet = (user) => {
    setSelectedUser(user);
    setShowLockConfirmModal(true);
  };

  const confirmToggleWallet = async () => {
    const user = selectedUser;
    const currentlyLocked = user.wallet_locked === true;

    try {
      const res = await fetch(`${API}/admin/souk-clothes/toggle-wallet`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
        body: JSON.stringify({
          user_id: user.user_id,
          lock: !currentlyLocked
        })
      });
      
      const result = await res.json();
      
      if (result.success) {
        showNotification(`Wallet ${result.locked ? 'locked' : 'unlocked'} successfully!`, 'success');
        setShowLockConfirmModal(false);
        fetchData(); // Refresh user list
      } else {
        showNotification('Error toggling wallet: ' + (result.message || 'Unknown error'), 'error');
      }
    } catch (error) {
      showNotification('Error toggling wallet: ' + error.message, 'error');
    }
  };

  const handleDelete = async (id, type) => {
    try {
      const res = await fetch(`${API}/admin/souk-clothes/${type}/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      const result = await res.json();
      if (result.success) {
        showNotification('Deleted successfully!', 'success');
        fetchData();
        setShowDeleteConfirm(false);
      }
    } catch (error) {
      showNotification('Error deleting: ' + error.message, 'error');
    }
  };


  const freeMarketSections = [
    { id: 'fm-settings', label: 'Free Market Settings', icon: '⚙️' },
    { id: 'merchants', label: 'Merchants', icon: '🏪' },
    { id: 'categories', label: 'Item Categories', icon: '📁' },
    { id: 'items', label: 'Clothing Items', icon: '👕' },
    { id: 'trading', label: 'Trading', icon: '🤝' },
    { id: 'marketplace', label: 'Marketplace', icon: '🏪' },
    { id: 'dressup', label: 'Dress-Up Avatar', icon: '🎨' },
    { id: 'avatars', label: 'Avatar Images', icon: '👤' },
    { id: 'users', label: 'User Wallets', icon: '💰' },
    { id: 'vocab', label: 'Vocabulary', icon: '📖' },
    { id: 'analytics', label: 'Analytics', icon: '📊' },
  ];

  const missionSections = [
    { id: 'missions', label: 'Outfit Missions', icon: '🎯' },
    { id: 'settings', label: 'Game Settings', icon: '⚙️' },
    { id: 'areas', label: 'Souk Areas', icon: '🗺️' },
    { id: 'merchants', label: 'Merchants', icon: '🏪' },
    { id: 'categories', label: 'Item Categories', icon: '📁' },
    { id: 'items', label: 'Clothing Items', icon: '👕' },
    { id: 'users', label: 'User Wallets', icon: '💰' },
    { id: 'vocab', label: 'Vocabulary', icon: '📖' },
    { id: 'analytics', label: 'Analytics', icon: '📊' },
  ];

  const sections = gameTab === 'free-market' ? freeMarketSections : missionSections;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className={`rounded-lg p-6 ${isDarkMode ? 'bg-gray-800' : 'bg-white'} border ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
        <h2 className={`text-2xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
          👗 Souk L7wayej Game Management
        </h2>
        <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
          Manage all aspects of the Clothes Shopping Game + Learning Features
        </p>

        {/* Game Card Text */}
        {settings && (
          <div className={`mt-4 pt-4 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <div className="flex items-end gap-3 mb-3">
              <div className="flex-1">
                <label className={`text-xs font-semibold ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Game Name</label>
                <input
                  value={settings.name || ''}
                  onChange={e => setSettings({...settings, name: e.target.value})}
                  className={`w-full px-3 py-1.5 rounded-lg text-sm ${isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-800 border-gray-300'} border focus:border-blue-500 focus:outline-none`}
                />
              </div>
              <div className="flex-1">
                <label className={`text-xs font-semibold ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Description</label>
                <input
                  value={settings.description || ''}
                  onChange={e => setSettings({...settings, description: e.target.value})}
                  className={`w-full px-3 py-1.5 rounded-lg text-sm ${isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-800 border-gray-300'} border focus:border-blue-500 focus:outline-none`}
                />
              </div>
              <button
                onClick={() => handleSave(settings, '/admin/souk-clothes/settings')}
                className="px-3 py-1.5 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm font-semibold whitespace-nowrap"
              >
                Save Text
              </button>
            </div>
          </div>
        )}

        {/* Global Game Toggles */}
        {settings && (
          <div className={`mt-2 pt-3 border-t flex items-center gap-6 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <span className={`text-xs font-bold ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>GLOBAL:</span>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={settings.is_active !== false}
                onChange={async (e) => {
                  const updated = { ...settings, is_active: e.target.checked };
                  setSettings(updated);
                  await handleSave(updated, '/admin/souk-clothes/settings');
                }}
                className="w-4 h-4"
              />
              <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>👁️ Visible in Games</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={settings.is_pro_only || false}
                onChange={async (e) => {
                  const updated = { ...settings, is_pro_only: e.target.checked };
                  setSettings(updated);
                  await handleSave(updated, '/admin/souk-clothes/settings');
                }}
                className="w-4 h-4"
              />
              <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>🔒 Pro Only (whole game)</span>
            </label>
          </div>
        )}
      </div>

      {/* Main Layout - Sidebar + Content */}
      <div className="flex gap-6">
        {/* Left Sidebar - Game Tabs + Vertical Navigation */}
        <div className={`w-64 flex-shrink-0 rounded-xl shadow-lg overflow-hidden ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
          {/* Top-level Game Mode Tabs */}
          <div className={`flex border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <button
              onClick={() => { setGameTab('free-market'); setActiveSection('fm-settings'); }}
              className={`flex-1 py-3 text-xs font-bold text-center transition-all ${
                gameTab === 'free-market'
                  ? isDarkMode ? 'bg-teal-700 text-white' : 'bg-teal-600 text-white'
                  : isDarkMode ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-50'
              }`}
            >
              🛍️ Free Market
            </button>
            <button
              onClick={() => { setGameTab('missions'); setActiveSection('missions'); }}
              className={`flex-1 py-3 text-xs font-bold text-center transition-all ${
                gameTab === 'missions'
                  ? isDarkMode ? 'bg-orange-700 text-white' : 'bg-orange-500 text-white'
                  : isDarkMode ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-50'
              }`}
            >
              🎯 Missions
            </button>
          </div>

          <div className="p-4">
            <h3 className={`text-xs font-bold mb-3 px-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              SECTIONS
            </h3>
            <div className="space-y-1">
              {sections.map(section => (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-all text-left text-sm ${
                    activeSection === section.id
                      ? isDarkMode
                        ? 'bg-purple-600 text-white shadow-lg'
                        : 'bg-purple-500 text-white shadow-lg'
                      : isDarkMode
                        ? 'text-gray-300 hover:bg-gray-700 hover:text-white'
                        : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                  }`}
                >
                  <span className="text-xl">{section.icon}</span>
                  <span>{section.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Right Content Area */}
        <div className={`flex-1 rounded-xl shadow-lg p-6 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>

      {/* Loading State */}
      {loading && (
        <div className={`text-center py-12 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          <div className="text-4xl mb-2 animate-spin">⏳</div>
          <p>Loading...</p>
        </div>
      )}

      {/* Content Sections */}
      {!loading && (
        <>
          {/* FREE MARKET SETTINGS SECTION */}
          {activeSection === 'fm-settings' && settings && (
            <FreeMarketSettingsEditor
              settings={settings}
              onSave={(data) => handleSave(data, '/admin/souk-clothes/settings')}
              isDarkMode={isDarkMode}
              API={API}
              adminToken={adminToken}
            />
          )}

          {/* SETTINGS SECTION */}
          {activeSection === 'settings' && settings && (
            <SettingsEditor
              settings={settings}
              onSave={(data) => handleSave(data, '/admin/souk-clothes/settings')}
              isDarkMode={isDarkMode}
              API={API}
              adminToken={adminToken}
            />
          )}

          {/* MERCHANTS SECTION */}
          {activeSection === 'merchants' && (
            <MerchantsTable
              merchants={merchants}
              onEdit={(merchant) => { setEditingItem(merchant); setShowEditModal(true); }}
              onDelete={(merchant) => { setDeleteTarget(merchant); setShowDeleteConfirm(true); }}
              onAdd={() => setShowAddModal(true)}
              isDarkMode={isDarkMode}
            />
          )}

          {/* CATEGORIES SECTION */}
          {activeSection === 'categories' && (
            <CategoriesTable
              categories={categories}
              onEdit={(category) => { setEditingItem(category); setShowEditModal(true); }}
              onDelete={(category) => { setDeleteTarget(category); setShowDeleteConfirm(true); }}
              onAdd={() => setShowAddModal(true)}
              isDarkMode={isDarkMode}
            />
          )}

          {/* ITEMS SECTION */}
          {activeSection === 'items' && (
            <ItemsTable
              items={items}
              merchants={merchants}
              onEdit={(item) => { setEditingItem(item); setShowEditModal(true); }}
              onDelete={(item) => { setDeleteTarget(item); setShowDeleteConfirm(true); }}
              onAdd={() => setShowAddModal(true)}
              isDarkMode={isDarkMode}
            />
          )}


          {/* DRESS-UP AVATAR LEARNING GAME SECTION */}
          {activeSection === 'dressup' && (
            <DressUpAdminInfo
              isDarkMode={isDarkMode}
              API={API}
              adminToken={adminToken}
              showNotification={showNotification}
            />
          )}


          {/* MISSIONS SECTION */}
          {activeSection === 'missions' && (
            <MissionsTable
              missions={missions}
              items={items}
              onEdit={(mission) => { setEditingItem(mission); setShowEditModal(true); }}
              onAdd={() => setShowAddModal(true)}
              onDelete={(mission) => { setDeleteTarget(mission); setShowDeleteConfirm(true); }}
              isDarkMode={isDarkMode}
            />
          )}

          {/* SOUK AREAS SECTION */}
          {activeSection === 'areas' && (
            <AreasTable
              areas={areas}
              onEdit={(area) => { setEditingItem(area); setShowEditModal(true); }}
              onDelete={(area) => { setDeleteTarget(area); setShowDeleteConfirm(true); }}
              onAdd={() => setShowAddModal(true)}
              isDarkMode={isDarkMode}
            />
          )}

          {/* USERS SECTION */}
          {activeSection === 'users' && (
            <UsersTable
              users={users}
              onAddCoins={(user) => { setSelectedUser(user); setShowAddCoinsModal(true); }}
              onEditCoins={(user) => { setSelectedUser(user); setShowEditCoinsModal(true); }}
              onToggleWallet={handleToggleWallet}
              isDarkMode={isDarkMode}
            />
          )}

          {/* ANALYTICS SECTION */}
          {activeSection === 'analytics' && analytics && (
            <AnalyticsTab
              analytics={analytics}
              isDarkMode={isDarkMode}
            />
          )}

          {/* TRADING SECTION — Free Market Only */}
          {activeSection === 'trading' && (
            <div className="space-y-6">
              {/* Header */}
              <div className={`rounded-lg p-5 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border`}>
                <h3 className={`text-lg font-bold mb-1 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                  🤝 Trading Management
                </h3>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Free Market trading — P2P and merchant trade activity
                </p>
              </div>

              {/* P2P Settings */}
              <div className={`rounded-lg p-5 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border`}>
                <h4 className={`text-sm font-bold mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>P2P Trading Settings</h4>
                <div className="flex flex-wrap items-center gap-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={tradingData?.settings?.enable_p2p_trading || false}
                      onChange={async (e) => {
                        try {
                          await fetch(`${API}/admin/souk-clothes/trading/settings`, {
                            method: 'PUT',
                            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
                            body: JSON.stringify({ enable_p2p_trading: e.target.checked })
                          });
                          showNotification(e.target.checked ? 'P2P Trading enabled' : 'P2P Trading disabled', 'success');
                          fetchData();
                        } catch (err) {
                          showNotification('Error updating setting', 'error');
                        }
                      }}
                      className="w-4 h-4"
                    />
                    <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Enable P2P Trading
                    </span>
                  </label>
                  <div className="flex items-center gap-2">
                    <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Trade Fee:</span>
                    <input
                      type="number"
                      min="0"
                      max="50"
                      value={tradingData?.settings?.p2p_trade_fee_pct ?? 10}
                      onChange={async (e) => {
                        try {
                          await fetch(`${API}/admin/souk-clothes/trading/settings`, {
                            method: 'PUT',
                            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
                            body: JSON.stringify({ p2p_trade_fee_pct: parseInt(e.target.value) || 10 })
                          });
                          fetchData();
                        } catch (err) {
                          showNotification('Error updating fee', 'error');
                        }
                      }}
                      className={`w-16 px-2 py-1 rounded border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                    />
                    <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>%</span>
                  </div>
                </div>
                {!tradingData?.p2p_enabled && (
                  <p className={`text-xs mt-3 ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`}>
                    P2P trading is currently disabled. Users cannot create or browse trade listings.
                  </p>
                )}
              </div>

              {/* Banned Words */}
              <div className={`rounded-lg p-5 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border`}>
                <h4 className={`text-sm font-bold mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  🚫 Banned Words
                </h4>
                <p className={`text-xs mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Words that get censored to ** in trade notes. Users who use them get flagged.
                </p>
                <div className="flex gap-2 mb-3">
                  <input
                    type="text"
                    value={newBannedWord}
                    onChange={e => setNewBannedWord(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' && newBannedWord.trim()) {
                        const newWords = newBannedWord.split(',').map(w => w.trim().toLowerCase()).filter(w => w && !bannedWords.includes(w));
                        if (newWords.length > 0) {
                          const updated = [...bannedWords, ...newWords];
                          setBannedWords(updated);
                          setNewBannedWord('');
                          fetch(`${API}/admin/souk-clothes/trading/banned-words`, {
                            method: 'PUT',
                            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
                            body: JSON.stringify({ banned_words: updated })
                          }).then(() => showNotification(`Added ${newWords.length} word${newWords.length > 1 ? 's' : ''}`, 'success'))
                            .catch(() => showNotification('Error saving', 'error'));
                        }
                      }
                    }}
                    placeholder="Type words separated by commas, then Enter..."
                    className={`flex-1 px-3 py-2 rounded-lg border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-500' : 'bg-white border-gray-300 placeholder-gray-400'}`}
                  />
                  <button
                    onClick={() => {
                      if (newBannedWord.trim()) {
                        const newWords = newBannedWord.split(',').map(w => w.trim().toLowerCase()).filter(w => w && !bannedWords.includes(w));
                        if (newWords.length > 0) {
                          const updated = [...bannedWords, ...newWords];
                          setBannedWords(updated);
                          setNewBannedWord('');
                          fetch(`${API}/admin/souk-clothes/trading/banned-words`, {
                            method: 'PUT',
                            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
                            body: JSON.stringify({ banned_words: updated })
                          }).then(() => showNotification(`Added ${newWords.length} word${newWords.length > 1 ? 's' : ''}`, 'success'))
                            .catch(() => showNotification('Error saving', 'error'));
                        }
                      }
                    }}
                    className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-lg transition-colors"
                  >
                    Add
                  </button>
                </div>
                {bannedWords.length === 0 ? (
                  <p className={`text-xs text-center py-3 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>No banned words yet</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {bannedWords.map(word => (
                      <span
                        key={word}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${isDarkMode ? 'bg-red-900/40 text-red-300' : 'bg-red-50 text-red-700 border border-red-200'}`}
                      >
                        {word}
                        <button
                          onClick={() => {
                            const updated = bannedWords.filter(w => w !== word);
                            setBannedWords(updated);
                            fetch(`${API}/admin/souk-clothes/trading/banned-words`, {
                              method: 'PUT',
                              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
                              body: JSON.stringify({ banned_words: updated })
                            }).then(() => showNotification(`Removed "${word}"`, 'success'))
                              .catch(() => showNotification('Error saving', 'error'));
                          }}
                          className="text-red-400 hover:text-red-600 font-bold"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Flagged Messages */}
              {flaggedMessages.length > 0 && (
                <div className={`rounded-lg p-5 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border`}>
                  <h4 className={`text-sm font-bold mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    🚩 Flagged Messages ({flaggedMessages.length})
                  </h4>
                  <p className={`text-xs mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Users who tried to use banned words in trade notes
                  </p>
                  <div className="space-y-2 max-h-[40vh] overflow-y-auto">
                    {flaggedMessages.map(msg => (
                      <div key={msg.id} className={`rounded-lg p-3 ${isDarkMode ? 'bg-gray-700' : 'bg-red-50 border border-red-100'}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`text-xs font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                                {msg.marketplace_name || msg.username}
                              </span>
                              <span className={`text-[10px] px-1.5 py-0.5 rounded ${isDarkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-600'}`}>
                                {msg.context === 'create_listing' ? 'New Listing' : 'Edit Listing'}
                              </span>
                              <span className={`text-[10px] ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                {msg.created_at ? new Date(msg.created_at).toLocaleDateString() : ''}
                              </span>
                            </div>
                            <p className={`text-xs mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                              "{msg.original_text}"
                            </p>
                            <div className="flex flex-wrap gap-1">
                              {(msg.found_words || []).map((w, i) => (
                                <span key={i} className="text-[10px] bg-red-500 text-white px-1.5 py-0.5 rounded font-medium">{w}</span>
                              ))}
                            </div>
                          </div>
                          <button
                            onClick={async () => {
                              try {
                                await fetch(`${API}/admin/souk-clothes/trading/flagged-messages/${msg.id}`, {
                                  method: 'DELETE',
                                  headers: { 'Authorization': `Bearer ${adminToken}` }
                                });
                                setFlaggedMessages(prev => prev.filter(m => m.id !== msg.id));
                                showNotification('Dismissed', 'success');
                              } catch {
                                showNotification('Error dismissing', 'error');
                              }
                            }}
                            className={`text-xs px-2 py-1 rounded ${isDarkMode ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-600' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-200'} transition-colors flex-shrink-0`}
                          >
                            Dismiss
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Trade Stats */}
              <div className={`rounded-lg p-5 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border`}>
                <h4 className={`text-sm font-bold mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Trade Statistics</h4>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
                  {[
                    { label: 'Total Listings', value: tradingData?.total_listings || 0, icon: '📋' },
                    { label: 'Pending Offers', value: tradingData?.trade_stats?.total_pending_offers || 0, icon: '⏳' },
                    { label: 'Completed Trades', value: tradingData?.trade_stats?.total_completed || 0, icon: '✅' },
                    { label: 'Fees Collected', value: `🪙 ${tradingData?.trade_stats?.total_fees_collected || 0}`, icon: '' },
                    { label: 'Merchant Trades', value: tradingData?.trade_stats?.merchant_trades || 0, icon: '🏪' },
                  ].map((stat, i) => (
                    <div key={i} className={`rounded-lg p-3 text-center ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                      {stat.icon && <span className="text-xl block mb-1">{stat.icon}</span>}
                      <p className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{stat.value}</p>
                      <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{stat.label}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Active P2P Listings */}
              <div className={`rounded-lg p-5 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border`}>
                <h4 className={`text-sm font-bold mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Active P2P Listings ({tradingData?.active_listings?.length || 0})
                </h4>
                {(!tradingData?.active_listings || tradingData.active_listings.length === 0) ? (
                  <div className={`text-center py-8 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    <span className="text-3xl block mb-2">🤝</span>
                    <p className="text-sm">No active trade listings</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {tradingData.active_listings.map((listing) => (
                      <div key={listing.id} className={`flex items-center justify-between p-3 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                        <div className="flex items-center gap-3">
                          {listing.item_image ? (
                            <img src={listing.item_image} alt="" className="w-10 h-10 rounded-lg object-cover" />
                          ) : (
                            <div className="w-10 h-10 rounded-lg bg-gray-200 flex items-center justify-center text-lg">👕</div>
                          )}
                          <div>
                            <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                              {listing.item_name || 'Unknown Item'}
                            </p>
                            <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              by {listing.anonymous_name || 'Anonymous'} • 🪙 {listing.item_value || 0} • Wants: {listing.wanted_description || 'Any'}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={async () => {
                            if (!window.confirm('Cancel this listing?')) return;
                            try {
                              await fetch(`${API}/admin/souk-clothes/trading/listings/${listing.id}`, {
                                method: 'DELETE',
                                headers: { 'Authorization': `Bearer ${adminToken}` }
                              });
                              showNotification('Listing cancelled', 'success');
                              fetchData();
                            } catch (err) {
                              showNotification('Error cancelling listing', 'error');
                            }
                          }}
                          className="text-xs px-3 py-1.5 rounded-lg bg-red-100 text-red-700 hover:bg-red-200 font-medium transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Pending Offers */}
              <div className={`rounded-lg p-5 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border`}>
                <h4 className={`text-sm font-bold mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  ⏳ Pending Offers ({tradingData?.pending_offers?.length || 0})
                </h4>
                {(!tradingData?.pending_offers || tradingData.pending_offers.length === 0) ? (
                  <div className={`text-center py-6 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    <p className="text-sm">No pending trade offers</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {tradingData.pending_offers.map((offer) => (
                      <div key={offer.id} className={`flex items-center justify-between p-3 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-amber-50'}`}>
                        <div>
                          <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                            {offer.offerer_name || 'User'} → Listing: {offer.listing_item_name || offer.listing_id?.slice(0, 8) || 'Item'}
                          </p>
                          <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Offering: {offer.offer_item_name || 'Item'} • {offer.created_at ? new Date(offer.created_at).toLocaleDateString() : ''}
                          </p>
                        </div>
                        <span className="text-xs px-2 py-1 rounded-full bg-amber-100 text-amber-700 font-medium">Pending</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Completed Trades */}
              <div className={`rounded-lg p-5 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border`}>
                <h4 className={`text-sm font-bold mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Recent Completed Trades ({tradingData?.completed_trades?.length || 0})
                </h4>
                {(!tradingData?.completed_trades || tradingData.completed_trades.length === 0) ? (
                  <div className={`text-center py-6 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    <p className="text-sm">No completed trades yet</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {tradingData.completed_trades.slice(0, 20).map((trade) => (
                      <div key={trade.id} className={`flex items-center justify-between p-3 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                        <div>
                          <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                            {trade.item_name || 'Item'} → {trade.accepted_by_name || 'Trader'}
                          </p>
                          <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            🪙 {trade.item_value || 0} value • Fee: 🪙 {trade.fee_collected || 0} • {trade.completed_at ? new Date(trade.completed_at).toLocaleDateString() : ''}
                          </p>
                        </div>
                        <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-700 font-medium">Completed</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* MARKETPLACE TEXT SETTINGS */}
          {activeSection === 'marketplace' && settings && (() => {
            const inputClass = `w-full px-2 py-1 rounded border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`;
            const labelClass = `block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`;
            const secClass = `p-3 rounded-lg border ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`;
            const hdClass = `font-bold text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`;

            const mpSections = [
              { title: 'General', icon: '🏪', fields: [
                { key: 'mp_title', label: 'Page Title', d: 'Marketplace' },
                { key: 'mp_back_button', label: 'Back Button', d: 'Back' },
                { key: 'mp_list_button', label: 'List/Sell Button', d: '+ Sell' },
                { key: 'mp_search_placeholder', label: 'Search Placeholder', d: 'Search items...' },
                { key: 'mp_loading', label: 'Loading Text', d: 'Loading marketplace...' },
                { key: 'mp_anonymous_note', label: 'Anonymous Note', d: 'Your identity is hidden — traders see your marketplace name' },
                { key: 'icon_marketplace', label: 'Marketplace Icon', d: '🏪' },
                { key: 'icon_notifications', label: 'Notifications Icon', d: '🔔' },
              ]},
              { title: 'Categories', icon: '📂', fields: [
                { key: 'mp_cat_all', label: 'All', d: 'All' },
                { key: 'mp_cat_tops', label: 'Tops', d: 'Tops' },
                { key: 'mp_cat_bottoms', label: 'Bottoms', d: 'Bottoms' },
                { key: 'mp_cat_shoes', label: 'Shoes', d: 'Shoes' },
                { key: 'mp_cat_accessories', label: 'Accessories', d: 'Accessories' },
                { key: 'mp_cat_outerwear', label: 'Outerwear', d: 'Outerwear' },
              ]},
              { title: 'Browse & Sort', icon: '🔍', fields: [
                { key: 'mp_your_listings_title', label: 'Your Listings Title', d: 'Your Listings' },
                { key: 'mp_sort_newest', label: 'Sort: Newest', d: 'Newest' },
                { key: 'mp_sort_cheapest', label: 'Sort: Cheapest', d: 'Cheapest' },
                { key: 'mp_sort_popular', label: 'Sort: Popular', d: 'Most Popular' },
                { key: 'mp_items_label', label: 'Items (plural)', d: 'items' },
                { key: 'mp_item_label', label: 'Item (singular)', d: 'item' },
                { key: 'mp_hot_badge', label: 'Hot Badge', d: 'HOT' },
                { key: 'mp_already_listed', label: 'Already Listed', d: 'Listed' },
              ]},
              { title: 'Empty States', icon: '📭', fields: [
                { key: 'mp_empty_title', label: 'Empty Title', d: 'No listings yet' },
                { key: 'mp_empty_desc', label: 'Empty Desc', d: 'Be the first to list an item!' },
                { key: 'mp_no_others_title', label: 'No Others Title', d: 'No other listings yet' },
                { key: 'mp_no_others_desc', label: 'No Others Desc', d: 'Check back soon for new items from other traders!' },
              ]},
              { title: 'Item Detail', icon: '👁️', fields: [
                { key: 'mp_by_label', label: 'By Label', d: 'by' },
                { key: 'mp_from_label', label: 'From Label', d: 'from' },
                { key: 'mp_default_trader', label: 'Default Trader Name', d: 'Trader' },
                { key: 'mp_views_label', label: 'Views Label', d: 'views' },
                { key: 'mp_seller_wants', label: 'Seller Wants', d: 'Seller wants' },
                { key: 'mp_accepts_items', label: 'Accepts Items', d: '🤝 Accepts item trades' },
                { key: 'mp_tokens_label', label: 'Tokens Label', d: 'tokens' },
                { key: 'mp_trade_only_label', label: 'Trade Only', d: '🤝 Trade' },
                { key: 'mp_trade_my_item', label: 'Trade My Item', d: '🤝 Trade My Item' },
                { key: 'mp_buy_for_label', label: 'Buy For', d: 'Buy for' },
              ]},
              { title: 'Create Listing', icon: '📝', fields: [
                { key: 'mp_pick_item_title', label: 'Pick Item Title', d: 'Pick an Item to List' },
                { key: 'mp_set_trade_options', label: 'Trade Options Title', d: 'Set Trade Options' },
                { key: 'mp_how_to_trade', label: 'How To Trade', d: 'How do you want to trade?' },
                { key: 'mp_accept_trades_label', label: 'Accept Trades Label', d: 'Accept item trades' },
                { key: 'mp_token_price_label', label: 'Token Price Label', d: 'Set token price' },
                { key: 'mp_wanted_desc_label', label: 'Wanted Desc Label', d: 'What are you looking for?' },
                { key: 'mp_wanted_desc_placeholder', label: 'Wanted Desc Placeholder', d: 'e.g. Looking for a leather jacket...' },
                { key: 'mp_confirm_list', label: 'Confirm List Button', d: 'List on Marketplace' },
                { key: 'mp_free_trade_note', label: 'Free Trade Note', d: 'Free — no listing fees!' },
                { key: 'mp_no_contact_info', label: 'No Contact Warning', d: 'No emails, phone numbers, or social media' },
              ]},
              { title: 'Buying', icon: '💰', fields: [
                { key: 'mp_buy_confirm_title', label: 'Buy Confirm Title', d: 'Buy' },
                { key: 'mp_price_label', label: 'Price Label', d: 'Price' },
                { key: 'mp_balance_label', label: 'Balance Label', d: 'Your balance' },
                { key: 'mp_not_enough_tokens', label: 'Not Enough', d: 'Not enough tokens!' },
                { key: 'mp_buying', label: 'Buying Text', d: 'Buying...' },
                { key: 'mp_purchased_title', label: 'Purchased Title', d: 'Purchased!' },
                { key: 'mp_you_got', label: 'You Got', d: 'You got' },
                { key: 'mp_paid_label', label: 'Paid Label', d: 'Paid' },
              ]},
              { title: 'Offers', icon: '🤝', fields: [
                { key: 'mp_send_offer_title', label: 'Send Offer Title', d: 'Send Trade Offer' },
                { key: 'mp_theyre_trading', label: 'Theyre Trading', d: "They're trading:" },
                { key: 'mp_your_offer', label: 'Your Offer', d: 'Your offer:' },
                { key: 'mp_send_offer_button', label: 'Send Offer Button', d: 'Send Offer' },
                { key: 'mp_sending', label: 'Sending Text', d: 'Sending...' },
                { key: 'mp_offer_sent_title', label: 'Offer Sent Title', d: 'Offer Sent!' },
                { key: 'mp_offer_sent_will_notify', label: 'Will Notify', d: 'will be notified.' },
                { key: 'mp_offer_sent_desc', label: 'Offer Sent Desc', d: "They can accept or decline. You'll get a notification either way!" },
                { key: 'mp_offers_plural', label: 'Offers (plural)', d: 'offers' },
                { key: 'mp_offer_singular', label: 'Offer (singular)', d: 'offer' },
                { key: 'mp_person_interested', label: 'Person Interested', d: 'person already interested' },
                { key: 'mp_people_interested', label: 'People Interested', d: 'people already interested' },
              ]},
              { title: 'Edit & Cancel', icon: '✏️', fields: [
                { key: 'mp_edit_button', label: 'Edit Button', d: 'Edit' },
                { key: 'mp_edit_listing_title', label: 'Edit Listing Title', d: 'Edit Listing' },
                { key: 'mp_saving', label: 'Saving Text', d: 'Saving...' },
                { key: 'mp_cancel_listing_button', label: 'Cancel Listing Button', d: 'Cancel' },
                { key: 'mp_cancel_confirm_title', label: 'Cancel Confirm Title', d: 'Cancel Listing?' },
                { key: 'mp_cancel_confirm_desc', label: 'Cancel Confirm Desc', d: 'This will remove your listing from the marketplace.' },
                { key: 'mp_cancelling', label: 'Cancelling Text', d: 'Cancelling...' },
                { key: 'mp_keep_listing', label: 'Keep Listing', d: 'Keep Listing' },
              ]},
              { title: 'Trade Panel', icon: '📋', fields: [
                { key: 'tp_title', label: 'Panel Title', d: 'Trade Panel' },
                { key: 'tp_button', label: 'Panel Button', d: 'Trade Panel' },
                { key: 'tp_cancel_listing', label: 'Cancel Listing', d: 'Cancel' },
                { key: 'tp_views_plural', label: 'Views (plural)', d: 'views' },
                { key: 'tp_view_singular', label: 'View (singular)', d: 'view' },
              ]},
              { title: 'Notifications', icon: '🔔', fields: [
                { key: 'mp_notifications_title', label: 'Title', d: 'Notifications' },
                { key: 'mp_no_notif_title', label: 'No Notif Title', d: 'No notifications yet' },
                { key: 'mp_no_notif_desc', label: 'No Notif Desc', d: "You'll see trade offers and updates here" },
              ]},
              { title: 'Success Messages', icon: '🎉', fields: [
                { key: 'mp_listed_title', label: 'Listed Title', d: 'Listed!' },
                { key: 'mp_listed_desc', label: 'Listed Desc', d: 'is now on the marketplace' },
                { key: 'mp_listed_note', label: 'Listed Note', d: 'Other traders can now see and offer for your item' },
                { key: 'mp_back_to_marketplace', label: 'Back Button', d: 'Back to Marketplace' },
                { key: 'mp_close_button', label: 'Close Button', d: 'Close' },
                { key: 'mp_cancel_button', label: 'Cancel Button', d: 'Cancel' },
              ]},
              { title: 'Errors', icon: '⚠️', fields: [
                { key: 'mp_error_create_listing', label: 'Create Listing Error', d: 'Failed to create listing' },
                { key: 'mp_error_send_offer', label: 'Send Offer Error', d: 'Failed to send offer' },
                { key: 'mp_error_buy', label: 'Buy Error', d: 'Failed to buy' },
                { key: 'mp_error_respond', label: 'Respond Error', d: 'Failed to respond' },
                { key: 'mp_error_edit_listing', label: 'Edit Listing Error', d: 'Failed to update listing' },
                { key: 'mp_validation_trade_or_price', label: 'Validation: Trade/Price', d: 'Must accept item trades or set a token price' },
              ]},
              { title: 'Closet Hint', icon: '👗', fields: [
                { key: 'closet_hint', label: 'Closet Hint Text', d: 'Tap an item to sell or trade it' },
              ]},
              { title: 'Word of the Day', icon: '📖', fields: [
                { key: 'mp_word_of_day_label', label: 'Section Title', d: 'Word of the Day' },
              ]},
              { title: 'Darija Action Phrases', icon: '🇲🇦', fields: [
                { key: 'action_phrase_buy_darija', label: 'Buy — Latin', d: 'Shritiha!' },
                { key: 'action_phrase_buy_arabic', label: 'Buy — Arabic', d: 'شريتيها!' },
                { key: 'action_phrase_buy_meaning', label: 'Buy — English', d: 'You bought it!' },
                { key: 'action_phrase_sell_darija', label: 'Sell — Latin', d: 'Tba3tiha!' },
                { key: 'action_phrase_sell_arabic', label: 'Sell — Arabic', d: 'تبعتيها!' },
                { key: 'action_phrase_sell_meaning', label: 'Sell — English', d: 'You sold it!' },
                { key: 'action_phrase_trade_darija', label: 'Trade — Latin', d: 'Tbadltiha!' },
                { key: 'action_phrase_trade_arabic', label: 'Trade — Arabic', d: 'تبدلتيها!' },
                { key: 'action_phrase_trade_meaning', label: 'Trade — English', d: 'You traded it!' },
                { key: 'action_phrase_list_darija', label: 'List — Latin', d: 'Dkhliha f souk!' },
                { key: 'action_phrase_list_arabic', label: 'List — Arabic', d: 'دخليها ف السوق!' },
                { key: 'action_phrase_list_meaning', label: 'List — English', d: 'Put it in the souk!' },
                { key: 'action_phrase_offer_darija', label: 'Offer — Latin', d: 'Sift l3ard!' },
                { key: 'action_phrase_offer_arabic', label: 'Offer — Arabic', d: 'سيفت العرض!' },
                { key: 'action_phrase_offer_meaning', label: 'Offer — English', d: 'Sent the offer!' },
              ]},
            ];

            return (
              <div className="space-y-4">
                <div className={`rounded-lg p-5 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border`}>
                  <h3 className={`text-lg font-bold mb-1 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                    🏪 Marketplace Text Settings
                  </h3>
                  <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Edit all text shown in the P2P Marketplace. Changes apply instantly after save.
                  </p>

                  {mpSections.map(section => (
                    <details key={section.title} className={`${secClass} mb-2`}>
                      <summary className={`${hdClass} cursor-pointer select-none`}>{section.icon} {section.title}</summary>
                      <div className="grid grid-cols-2 gap-2 mt-3">
                        {section.fields.map(({ key, label, d }) => (
                          <div key={key}>
                            <label className={labelClass}>{label}</label>
                            <input
                              type="text"
                              value={settings[key] || d}
                              onChange={(e) => setSettings({ ...settings, [key]: e.target.value })}
                              className={inputClass}
                            />
                          </div>
                        ))}
                      </div>
                    </details>
                  ))}

                  {/* Darija Daily Phrases — list editor */}
                  <details className={`${secClass} mb-2`}>
                    <summary className={`${hdClass} cursor-pointer select-none`}>🇲🇦 Darija Daily Phrases ({darijaPhrases.length})</summary>
                    <p className={`text-xs mt-2 mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      These show as "Word of the Day" at the top of the Marketplace page. One phrase per day (rotates). Add, edit, or remove below.
                    </p>

                    {/* Add new phrase */}
                    <div className="grid grid-cols-3 gap-2 mb-3">
                      <input
                        type="text"
                        value={newPhrase.darija}
                        onChange={e => setNewPhrase({ ...newPhrase, darija: e.target.value })}
                        placeholder="Latin (e.g. Bssaha!)"
                        className={inputClass}
                      />
                      <input
                        type="text"
                        value={newPhrase.arabic}
                        onChange={e => setNewPhrase({ ...newPhrase, arabic: e.target.value })}
                        placeholder="Arabic (e.g. بالصحة!)"
                        className={inputClass}
                      />
                      <input
                        type="text"
                        value={newPhrase.meaning}
                        onChange={e => setNewPhrase({ ...newPhrase, meaning: e.target.value })}
                        placeholder="English meaning"
                        className={inputClass}
                        onKeyDown={e => {
                          if (e.key === 'Enter' && newPhrase.darija.trim() && newPhrase.meaning.trim()) {
                            setDarijaPhrases([...darijaPhrases, { darija: newPhrase.darija.trim(), arabic: newPhrase.arabic.trim(), meaning: newPhrase.meaning.trim() }]);
                            setNewPhrase({ darija: '', arabic: '', meaning: '' });
                          }
                        }}
                      />
                    </div>
                    <button
                      onClick={() => {
                        if (newPhrase.darija.trim() && newPhrase.meaning.trim()) {
                          setDarijaPhrases([...darijaPhrases, { darija: newPhrase.darija.trim(), arabic: newPhrase.arabic.trim(), meaning: newPhrase.meaning.trim() }]);
                          setNewPhrase({ darija: '', arabic: '', meaning: '' });
                        }
                      }}
                      className="mb-3 px-4 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-medium rounded-lg transition-colors"
                    >
                      + Add Phrase
                    </button>

                    {/* Existing phrases list */}
                    {darijaPhrases.length === 0 ? (
                      <p className={`text-xs text-center py-3 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>No phrases yet</p>
                    ) : (
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        {darijaPhrases.map((ph, idx) => (
                          <div key={idx} className={`flex items-center gap-2 p-2 rounded-lg ${isDarkMode ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                            <div className="flex-1 grid grid-cols-3 gap-2">
                              <input
                                type="text"
                                value={ph.darija}
                                onChange={e => {
                                  const updated = [...darijaPhrases];
                                  updated[idx] = { ...updated[idx], darija: e.target.value };
                                  setDarijaPhrases(updated);
                                }}
                                className={`${inputClass} text-xs`}
                              />
                              <input
                                type="text"
                                value={ph.arabic}
                                onChange={e => {
                                  const updated = [...darijaPhrases];
                                  updated[idx] = { ...updated[idx], arabic: e.target.value };
                                  setDarijaPhrases(updated);
                                }}
                                className={`${inputClass} text-xs`}
                              />
                              <input
                                type="text"
                                value={ph.meaning}
                                onChange={e => {
                                  const updated = [...darijaPhrases];
                                  updated[idx] = { ...updated[idx], meaning: e.target.value };
                                  setDarijaPhrases(updated);
                                }}
                                className={`${inputClass} text-xs`}
                              />
                            </div>
                            <button
                              onClick={() => setDarijaPhrases(darijaPhrases.filter((_, i) => i !== idx))}
                              className="text-red-400 hover:text-red-600 font-bold text-lg px-1"
                              title="Remove phrase"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </details>

                  <button
                    onClick={() => {
                      // Fill any missing keys with defaults before saving
                      const filled = { ...settings, darija_phrases: darijaPhrases };
                      mpSections.forEach(sec => sec.fields.forEach(({ key, d }) => {
                        if (!filled[key]) filled[key] = d;
                      }));
                      setSettings(filled);
                      handleSave(filled, '/admin/souk-clothes/settings');
                    }}
                    className="mt-4 w-full py-2.5 rounded-lg bg-teal-600 hover:bg-teal-700 text-white font-bold text-sm transition-colors"
                  >
                    💾 Save Marketplace Settings
                  </button>
                </div>
              </div>
            );
          })()}

          {/* VOCABULARY MANAGEMENT SECTION */}
          {activeSection === 'vocab' && (
            <div className="space-y-4">
              <div className={`rounded-lg p-5 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border`}>
                <h3 className={`text-lg font-bold mb-1 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                  📖 Vocabulary Management
                </h3>
                <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Pre-loaded words shown in the "Words I Learned" popup. Users see these as available words to learn during gameplay.
                </p>

                {/* Tab toggle */}
                <div className="flex gap-2 mb-4">
                  {[
                    { id: 'marketplace', label: '🏪 Marketplace', desc: 'Trading & browsing words' },
                    { id: 'missions', label: '🎯 Missions', desc: 'Bargaining & shopping words' },
                  ].map(tab => (
                    <button
                      key={tab.id}
                      onClick={() => setVocabTab(tab.id)}
                      className={`flex-1 py-2.5 px-3 rounded-lg text-sm font-bold transition-colors ${
                        vocabTab === tab.id
                          ? 'bg-teal-600 text-white shadow-sm'
                          : isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>

                {/* Vocab visibility toggles */}
                <div className={`flex gap-3 mb-4 p-3 rounded-lg ${isDarkMode ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                  <label className="flex items-center gap-2 flex-1 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings?.vocab_enabled_marketplace !== false}
                      onChange={(e) => {
                        const updated = { ...settings, vocab_enabled_marketplace: e.target.checked };
                        setSettings(updated);
                        handleSave(updated, '/admin/souk-clothes/settings');
                      }}
                      className="w-4 h-4 accent-teal-500"
                    />
                    <span className={`text-xs font-bold ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      🏪 Show vocab in Marketplace
                    </span>
                  </label>
                  <label className="flex items-center gap-2 flex-1 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings?.vocab_enabled_missions !== false}
                      onChange={(e) => {
                        const updated = { ...settings, vocab_enabled_missions: e.target.checked };
                        setSettings(updated);
                        handleSave(updated, '/admin/souk-clothes/settings');
                      }}
                      className="w-4 h-4 accent-teal-500"
                    />
                    <span className={`text-xs font-bold ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      🎯 Show vocab in Missions
                    </span>
                  </label>
                </div>

                {/* Display settings */}
                <div className={`p-3 rounded-lg border mb-4 ${isDarkMode ? 'border-gray-700 bg-gray-700/50' : 'border-gray-200 bg-gray-50'}`}>
                  <p className={`text-xs font-bold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Display Settings</p>
                  <div className="flex items-center gap-4 flex-wrap">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings?.vocab_shuffle === true}
                        onChange={e => {
                          setSettings(prev => ({ ...prev, vocab_shuffle: e.target.checked }));
                        }}
                        className="w-4 h-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                      />
                      <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Shuffle words (random order each time)
                      </span>
                    </label>
                    <div className="flex items-center gap-2">
                      <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Show</span>
                      <input
                        type="number"
                        min="1"
                        max="50"
                        value={settings?.vocab_limit || 8}
                        onChange={e => {
                          setSettings(prev => ({ ...prev, vocab_limit: parseInt(e.target.value) || 8 }));
                        }}
                        className={`w-16 px-2 py-1 rounded border text-sm text-center ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                      />
                      <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>words per tab</span>
                    </div>
                    <button
                      onClick={() => {
                        handleSave(settings, '/admin/souk-clothes/settings');
                        showNotification('Settings saved!', 'success');
                      }}
                      className="ml-auto px-4 py-1.5 bg-teal-600 hover:bg-teal-700 text-white text-xs font-bold rounded-lg transition-colors"
                    >
                      Save
                    </button>
                  </div>
                </div>

                {/* Add new word */}
                <div className={`p-3 rounded-lg border mb-4 ${isDarkMode ? 'border-gray-700 bg-gray-700/50' : 'border-gray-200 bg-gray-50'}`}>
                  <p className={`text-xs font-bold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Add New Word</p>
                  <div className="grid grid-cols-4 gap-2 mb-2">
                    <input
                      type="text"
                      value={newVocab.word}
                      onChange={e => setNewVocab({ ...newVocab, word: e.target.value })}
                      placeholder="Darija (Latin)"
                      className={`px-2 py-1.5 rounded border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                    />
                    <input
                      type="text"
                      value={newVocab.arabic}
                      onChange={e => setNewVocab({ ...newVocab, arabic: e.target.value })}
                      placeholder="Arabic"
                      className={`px-2 py-1.5 rounded border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                    />
                    <input
                      type="text"
                      value={newVocab.meaning}
                      onChange={e => setNewVocab({ ...newVocab, meaning: e.target.value })}
                      placeholder="English meaning"
                      className={`px-2 py-1.5 rounded border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                    />
                    <select
                      value={newVocab.category}
                      onChange={e => setNewVocab({ ...newVocab, category: e.target.value })}
                      className={`px-2 py-1.5 rounded border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                    >
                      <option value="phrase">💬 Phrase</option>
                      <option value="color">🎨 Color</option>
                      <option value="size">📏 Size</option>
                      <option value="clothing">👕 Clothing</option>
                      <option value="greeting">👋 Greeting</option>
                    </select>
                  </div>
                  <button
                    onClick={async () => {
                      if (!newVocab.word.trim() || !newVocab.meaning.trim()) return;
                      try {
                        const res = await fetch(`${API}/admin/souk-clothes/vocab-words`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
                          body: JSON.stringify({ ...newVocab, tab: vocabTab })
                        });
                        if (res.ok) {
                          const created = await res.json();
                          setAdminVocab(prev => [...prev, created]);
                          setNewVocab({ word: '', meaning: '', arabic: '', category: 'phrase' });
                          showNotification('Word added!', 'success');
                        }
                      } catch { showNotification('Error adding word', 'error'); }
                    }}
                    disabled={!newVocab.word.trim() || !newVocab.meaning.trim()}
                    className="px-4 py-1.5 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-40 text-white text-xs font-bold rounded-lg transition-colors"
                  >
                    + Add Word
                  </button>
                </div>

                {/* Word list for current tab */}
                {(() => {
                  const tabWords = adminVocab.filter(w => w.tab === vocabTab);
                  if (tabWords.length === 0) return (
                    <p className={`text-center py-8 text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      No {vocabTab} words yet. Add some above!
                    </p>
                  );

                  // Group by category
                  const grouped = {};
                  tabWords.forEach(w => {
                    const cat = w.category || 'phrase';
                    if (!grouped[cat]) grouped[cat] = [];
                    grouped[cat].push(w);
                  });

                  const catLabels = { phrase: '💬 Phrases', color: '🎨 Colors', size: '📏 Sizes', clothing: '👕 Clothing', greeting: '👋 Greetings' };

                  return (
                    <div className="space-y-4 max-h-[400px] overflow-y-auto">
                      {Object.entries(grouped).map(([cat, words]) => (
                        <div key={cat}>
                          <p className={`text-xs font-bold uppercase tracking-wider mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            {catLabels[cat] || cat} ({words.length})
                          </p>
                          <div className="space-y-1.5">
                            {words.map(w => (
                              <div key={w.id} className={`flex items-center gap-2 p-2 rounded-lg ${isDarkMode ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                                <div className="flex-1 grid grid-cols-3 gap-2">
                                  <input
                                    type="text"
                                    value={w.word || ''}
                                    onChange={e => setAdminVocab(prev => prev.map(v => v.id === w.id ? { ...v, word: e.target.value } : v))}
                                    onBlur={() => {
                                      fetch(`${API}/admin/souk-clothes/vocab-words/${w.id}`, {
                                        method: 'PUT',
                                        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
                                        body: JSON.stringify({ word: w.word, meaning: w.meaning, arabic: w.arabic, category: w.category })
                                      });
                                    }}
                                    className={`px-2 py-1 rounded border text-xs ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                                  />
                                  <input
                                    type="text"
                                    value={w.arabic || ''}
                                    onChange={e => setAdminVocab(prev => prev.map(v => v.id === w.id ? { ...v, arabic: e.target.value } : v))}
                                    onBlur={() => {
                                      fetch(`${API}/admin/souk-clothes/vocab-words/${w.id}`, {
                                        method: 'PUT',
                                        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
                                        body: JSON.stringify({ word: w.word, meaning: w.meaning, arabic: w.arabic, category: w.category })
                                      });
                                    }}
                                    className={`px-2 py-1 rounded border text-xs ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                                  />
                                  <input
                                    type="text"
                                    value={w.meaning || ''}
                                    onChange={e => setAdminVocab(prev => prev.map(v => v.id === w.id ? { ...v, meaning: e.target.value } : v))}
                                    onBlur={() => {
                                      fetch(`${API}/admin/souk-clothes/vocab-words/${w.id}`, {
                                        method: 'PUT',
                                        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
                                        body: JSON.stringify({ word: w.word, meaning: w.meaning, arabic: w.arabic, category: w.category })
                                      });
                                    }}
                                    className={`px-2 py-1 rounded border text-xs ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                                  />
                                </div>
                                <button
                                  onClick={async () => {
                                    try {
                                      await fetch(`${API}/admin/souk-clothes/vocab-words/${w.id}`, {
                                        method: 'DELETE',
                                        headers: { 'Authorization': `Bearer ${adminToken}` }
                                      });
                                      setAdminVocab(prev => prev.filter(v => v.id !== w.id));
                                      showNotification('Word removed', 'success');
                                    } catch { showNotification('Error removing', 'error'); }
                                  }}
                                  className="text-red-400 hover:text-red-600 font-bold text-lg px-1"
                                  title="Remove word"
                                >
                                  ×
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()}

                <p className={`text-xs mt-4 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  Words auto-save when you edit them. They appear in the "Words I Learned" popup — dimmed until the user learns them through gameplay.
                </p>
              </div>
            </div>
          )}

          {/* AVATAR IMAGES SECTION */}
          {activeSection === 'avatars' && (
            <>
              <AvatarManagement
                avatars={avatars}
                API={API}
                adminToken={adminToken}
                isDarkMode={isDarkMode}
                showNotification={showNotification}
                onRefresh={() => fetchData('avatars')}
              />
              {/* Avatar Parts — Paper Doll Body + Head */}
              <AvatarPartsManager
                API={API}
                adminToken={adminToken}
                isDarkMode={isDarkMode}
                showNotification={showNotification}
              />
            </>
          )}
        </>
      )}
        </div>
        {/* End Right Content Area */}
      </div>
      {/* End Main Layout */}

      {/* Edit Modal */}
      {showEditModal && editingItem && (
        <EditModal
          item={editingItem}
          type={activeSection}
          merchants={merchants}
          categories={categories}
          areas={areas}
          allItems={items}
          onSave={(data) => handleSave(data, `/admin/souk-clothes/${activeSection}/${editingItem.id}`)}
          onClose={() => { setShowEditModal(false); setEditingItem(null); }}
          isDarkMode={isDarkMode}
        />
      )}

      {/* Add Modal */}
      {showAddModal && (
        <AddModal
          type={activeSection}
          merchants={merchants}
          categories={categories}
          areas={areas}
          allItems={items}
          onSave={(data) => handleSave(data, `/admin/souk-clothes/${activeSection}`, 'POST')}
          onClose={() => setShowAddModal(false)}
          isDarkMode={isDarkMode}
        />
      )}

      {/* Delete Confirmation */}
      {showDeleteConfirm && deleteTarget && (
        <DeleteConfirm
          item={deleteTarget}
          type={activeSection}
          onConfirm={() => {
            handleDelete(deleteTarget.id, activeSection);
          }}
          onCancel={() => { setShowDeleteConfirm(false); setDeleteTarget(null); }}
          isDarkMode={isDarkMode}
        />
      )}

      {/* Add Coins Modal */}
      {showAddCoinsModal && selectedUser && (
        <AddCoinsModal
          user={selectedUser}
          onSave={handleAddCoins}
          onClose={() => { setShowAddCoinsModal(false); setSelectedUser(null); }}
          isDarkMode={isDarkMode}
        />
      )}

      {/* Edit Coins Modal */}
      {showEditCoinsModal && selectedUser && (
        <EditCoinsModal
          user={selectedUser}
          onSave={handleEditCoins}
          onClose={() => { setShowEditCoinsModal(false); setSelectedUser(null); }}
          isDarkMode={isDarkMode}
        />
      )}

      {/* Lock Confirm Modal */}
      {showLockConfirmModal && selectedUser && (
        <LockConfirmModal
          user={selectedUser}
          onConfirm={confirmToggleWallet}
          onCancel={() => { setShowLockConfirmModal(false); setSelectedUser(null); }}
          isDarkMode={isDarkMode}
        />
      )}
    </div>
  );
};

// Settings Editor Component
const FreeMarketSettingsEditor = ({ settings, onSave, isDarkMode, API, adminToken }) => {
  const [iconPickerField, setIconPickerField] = useState(null);
  const [formData, setFormData] = useState({
    fm_is_active: settings.fm_is_active !== false,
    fm_is_pro_only: settings.fm_is_pro_only || false,
    show_default_svg_avatar: settings.show_default_svg_avatar !== false,
    closet_max_outfits: settings.closet_max_outfits || 8,
    closet_max_accessories: settings.closet_max_accessories || 6,
    free_market_trade_credit_pct: settings.free_market_trade_credit_pct || 50,
    // Souk overview text
    fm_title: settings.fm_title || 'Free Market',
    fm_subtitle: settings.fm_subtitle || 'Walk the souk and shop from any merchant',
    fm_header_icon: settings.fm_header_icon || '🏮',
    // Gate (entrance) text
    fm_gate_icon: settings.fm_gate_icon || '🏮',
    fm_gate_title: settings.fm_gate_title || 'Bab el Souk',
    fm_gate_subtitle: settings.fm_gate_subtitle || '{count} merchants await you',
    fm_enter_button: settings.fm_enter_button || 'Enter Souk',
    // Exit gate text
    fm_exit_title: settings.fm_exit_title || 'End of Souk',
    fm_exit_subtitle: settings.fm_exit_subtitle || "You've seen all the merchants!",
    // Shopping flow text — Darija
    fm_greeting_darija: settings.fm_greeting_darija || 'Marhba! Achno bghiti?',
    fm_greeting_english: settings.fm_greeting_english || 'Welcome! What do you want?',
    fm_category_prompt: settings.fm_category_prompt || 'What are you looking for?',
    fm_no_items_darija: settings.fm_no_items_darija || 'Smahli, ma3ndish {category}... Shuf shi 7aja okhra!',
    fm_no_items_english: settings.fm_no_items_english || "Sorry, I don't have {category}... Try something else!",
    fm_items_darija: settings.fm_items_darija || '3ndi {category} zwinin! Ashmen wa7ed 3jbek?',
    fm_items_english: settings.fm_items_english || 'I have nice {category}! Which one do you like?',
    fm_color_darija: settings.fm_color_darija || 'Achmen lon bghiti?',
    fm_color_english: settings.fm_color_english || 'What color do you want?',
    fm_size_darija: settings.fm_size_darija || 'Achmen qiyas bghiti?',
    fm_size_english: settings.fm_size_english || 'What size do you want?',
    fm_deal_darija: settings.fm_deal_darija || 'Bghiti tsaweb?',
    fm_deal_english: settings.fm_deal_english || 'Want to deal?',
    fm_purchase_darija: settings.fm_purchase_darija || 'Mabrouk! Hadi 7aja zwina 3lik!',
    fm_purchase_english: settings.fm_purchase_english || 'Congratulations! This looks great on you!',
    fm_bargain_button: settings.fm_bargain_button || 'Bargain! Saweb m3aya!',
    fm_buy_button: settings.fm_buy_button || 'Buy at full price',
    // Color discovery flow
    fm_color_ask_preference_darija: settings.fm_color_ask_preference_darija || 'Bghiti shi lon khas?',
    fm_color_ask_preference_english: settings.fm_color_ask_preference_english || 'Do you want a specific color?',
    fm_color_has_it_darija: settings.fm_color_has_it_darija || '3endi lon {color}! Hahowa!',
    fm_color_has_it_english: settings.fm_color_has_it_english || 'I have {color}! Here it is!',
    fm_color_no_has_darija: settings.fm_color_no_has_darija || 'Smahli, ma3ndish {color}. Walakin shuf had l-alwan...',
    fm_color_no_has_english: settings.fm_color_no_has_english || "Sorry, I don't have {color}. But look at these colors...",
    fm_color_great_choice_darija: settings.fm_color_great_choice_darija || 'Ikhtiyar zwin! Lon {color}!',
    fm_color_great_choice_english: settings.fm_color_great_choice_english || 'Great choice! {color}!',
    // Floating menu appearance
    fm_menu_mobile_size: settings.fm_menu_mobile_size || 'small',
    fm_menu_desktop_size: settings.fm_menu_desktop_size || 'medium',
    fm_menu_gap: settings.fm_menu_gap || 'tight',
    fm_menu_position: settings.fm_menu_position || 'upper-third',
    // Shopping flow button text
    fm_closet_button: settings.fm_closet_button || 'My Closet',
    fm_back_to_souk_button: settings.fm_back_to_souk_button || 'Back to Souk',
    fm_back_to_categories: settings.fm_back_to_categories || 'See Other Categories',
    fm_color_pick_label: settings.fm_color_pick_label || 'Pick a color to learn — Khtar lon:',
    fm_color_no_thanks: settings.fm_color_no_thanks || 'No thanks, I like this one',
    fm_color_continue: settings.fm_color_continue || 'Continue to buy {item}',
    fm_color_try_another: settings.fm_color_try_another || 'Try another color',
    fm_color_keep_item: settings.fm_color_keep_item || 'Keep my {item}',
    fm_full_price_label: settings.fm_full_price_label || 'Full price',
    fm_owned_warning: settings.fm_owned_warning || 'You already own this item',
    fm_no_tokens_warning: settings.fm_no_tokens_warning || 'Not enough tokens!',
    fm_item_purchased_title: settings.fm_item_purchased_title || 'Item Purchased!',
    fm_word_learned_label: settings.fm_word_learned_label || 'Word Learned',
    fm_added_to_collection: settings.fm_added_to_collection || 'Added to your collection',
    fm_keep_shopping: settings.fm_keep_shopping || 'Keep Shopping with {merchant}',
    fm_visit_another: settings.fm_visit_another || 'Visit Another Merchant',
    fm_open_closet: settings.fm_open_closet || 'Open Closet',
    // Game Icons
    icon_cart_menu: settings.icon_cart_menu || '🛒',
    icon_closet_menu: settings.icon_closet_menu || '👗',
    icon_wallet_menu: settings.icon_wallet_menu || '💰',
    icon_stats_menu: settings.icon_stats_menu || '📊',
    icon_style_menu: settings.icon_style_menu || '👤',
    icon_help_menu: settings.icon_help_menu || '❓',
    icon_token: settings.icon_token || '🪙',
    icon_streak: settings.icon_streak || '🔥',
    icon_gift: settings.icon_gift || '🎁',
    icon_headwear: settings.icon_headwear || '🎩',
    icon_tops: settings.icon_tops || '👕',
    icon_outerwear: settings.icon_outerwear || '🧥',
    icon_bottoms: settings.icon_bottoms || '👖',
    icon_shoes: settings.icon_shoes || '👟',
    icon_accessories: settings.icon_accessories || '👜',
    icon_area_main_alley: settings.icon_area_main_alley || '🏪',
    icon_area_side_alley: settings.icon_area_side_alley || '🧵',
    icon_area_hidden_alley: settings.icon_area_hidden_alley || '🕌',
    icon_area_night_souk: settings.icon_area_night_souk || '🌙',
    icon_area_rooftop: settings.icon_area_rooftop || '☀️',
    icon_occasion_wedding: settings.icon_occasion_wedding || '💍',
    icon_occasion_beach: settings.icon_occasion_beach || '🏖️',
    icon_occasion_winter: settings.icon_occasion_winter || '❄️',
    icon_occasion_school: settings.icon_occasion_school || '📚',
    icon_occasion_work: settings.icon_occasion_work || '💼',
    icon_occasion_casual: settings.icon_occasion_casual || '👕',
    icon_occasion_traditional: settings.icon_occasion_traditional || '🕌',
    icon_occasion_party: settings.icon_occasion_party || '🎉',
    icon_occasion_eid: settings.icon_occasion_eid || '🌙',
    icon_occasion_formal: settings.icon_occasion_formal || '🎩',
    icon_occasion_sport: settings.icon_occasion_sport || '⚽',
    icon_occasion_date: settings.icon_occasion_date || '💐',
    icon_default_merchant: settings.icon_default_merchant || '🧔',
    icon_default_item: settings.icon_default_item || '👕',
    icon_empty_state: settings.icon_empty_state || '🏪',
    icon_locked: settings.icon_locked || '🔒',
    icon_walking: settings.icon_walking || '🚶',
    icon_trade: settings.icon_trade || '🔄',
    icon_dashboard_stars: settings.icon_dashboard_stars || '⭐',
    icon_dashboard_saved: settings.icon_dashboard_saved || '💰',
    icon_dashboard_items: settings.icon_dashboard_items || '👗',
    icon_dashboard_streak: settings.icon_dashboard_streak || '🔥',
  });

  const [callouts, setCallouts] = useState(settings.fm_callout_phrases || [
    { darija: 'Aji, aji! 3endi 7wayej zwinin!', english: 'Come, come! I have nice clothes!' },
    { darija: 'Shuf hna! Ataman mezyanin!', english: 'Look here! Good prices!' },
    { darija: 'Ya salaam, dkhol shuf!', english: 'Wow, come in and look!' },
    { darija: 'Marhba bik! Shri m3aya!', english: 'Welcome! Shop with me!' },
    { darija: 'A khouya, 3endi ga3 ma bghiti!', english: 'Brother, I have everything you want!' },
    { darija: '7wayej jdad! Tssana shuf!', english: 'New clothes! Wait, look!' },
  ]);

  const [personalityLabels, setPersonalityLabels] = useState(settings.fm_personality_labels || {
    friendly: 'Friendly', tough: 'Tough', tricky: 'Tricky', generous: 'Generous', impatient: 'Impatient'
  });
  const [personalityTips, setPersonalityTips] = useState(settings.fm_personality_tips || {
    friendly: 'Easy to bargain with!', tough: 'Hard negotiator — be persistent!', tricky: 'Watch out for tricks!', generous: 'Might give you a good deal!', impatient: 'Be quick or lose the deal!'
  });
  const [sizeTranslations, setSizeTranslations] = useState(settings.fm_size_translations || {
    XS: 'sghir bzzaf', S: 'sghir', M: 'wsat', L: 'kbir', XL: 'kbir bzzaf', XXL: 'kbir kbir'
  });

  const handleSave = () => {
    onSave({ ...settings, ...formData, fm_callout_phrases: callouts, fm_personality_labels: personalityLabels, fm_personality_tips: personalityTips, fm_size_translations: sizeTranslations });
  };

  const inputClass = `w-full px-3 py-2 rounded-lg border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`;
  const labelClass = `text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`;
  const sectionClass = `p-4 rounded-lg border ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`;
  const headingClass = `text-sm font-bold mb-3 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`;
  const hintClass = `text-xs mt-0.5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`;

  return (
    <div className={`rounded-lg p-4 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border`}>
      <h3 className={`text-lg font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>🛍️ Free Market Settings</h3>

      <div className="space-y-4">
        {/* Visibility Toggles */}
        <div className="grid grid-cols-2 gap-2">
          <div className="flex items-center justify-between p-3 rounded-lg border" style={{ borderColor: isDarkMode ? '#374151' : '#e5e7eb' }}>
            <label className={labelClass}>👁️ Visible</label>
            <input type="checkbox" checked={formData.fm_is_active} onChange={(e) => setFormData({ ...formData, fm_is_active: e.target.checked })} className="w-5 h-5" />
          </div>
          <div className="flex items-center justify-between p-3 rounded-lg border" style={{ borderColor: isDarkMode ? '#374151' : '#e5e7eb' }}>
            <label className={labelClass}>🔒 Pro Only</label>
            <input type="checkbox" checked={formData.fm_is_pro_only} onChange={(e) => setFormData({ ...formData, fm_is_pro_only: e.target.checked })} className="w-5 h-5" />
          </div>
          <div className="flex items-center justify-between p-3 rounded-lg border" style={{ borderColor: isDarkMode ? '#374151' : '#e5e7eb' }}>
            <label className={labelClass}>👤 Show SVG Avatar</label>
            <input type="checkbox" checked={formData.show_default_svg_avatar} onChange={(e) => setFormData({ ...formData, show_default_svg_avatar: e.target.checked })} className="w-5 h-5" />
          </div>
        </div>

        {/* Closet Limits */}
        <div className={sectionClass}>
          <h4 className={headingClass}>👗 Closet Limits</h4>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Max Outfits</label>
              <input type="number" min="1" max="20" value={formData.closet_max_outfits} onChange={(e) => setFormData({ ...formData, closet_max_outfits: parseInt(e.target.value) || 8 })} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Max Accessories</label>
              <input type="number" min="1" max="20" value={formData.closet_max_accessories} onChange={(e) => setFormData({ ...formData, closet_max_accessories: parseInt(e.target.value) || 6 })} className={inputClass} />
            </div>
          </div>
        </div>

        {/* Trade Settings */}
        <div className={sectionClass}>
          <h4 className={headingClass}>🔄 Trade Settings</h4>
          <div className="flex items-center gap-2">
            <label className={labelClass}>Trade Credit %</label>
            <input type="number" min="10" max="90" value={formData.free_market_trade_credit_pct} onChange={(e) => setFormData({ ...formData, free_market_trade_credit_pct: parseInt(e.target.value) || 50 })} className={inputClass} style={{ maxWidth: 80 }} />
            <span className={hintClass}>%</span>
          </div>
          <p className={hintClass}>50% means a 400-token item gives 200 trade credit</p>
        </div>

        {/* Souk Overview Text */}
        <div className={sectionClass}>
          <h4 className={headingClass}>🏪 Souk Page Text</h4>
          <div className="space-y-2">
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className={labelClass}>Header Icon</label>
                <input type="text" value={formData.fm_header_icon} onChange={(e) => setFormData({ ...formData, fm_header_icon: e.target.value })} className={inputClass} placeholder="🏮" />
                <p className={hintClass}>Emoji shown in page header</p>
              </div>
              <div>
                <label className={labelClass}>Title</label>
                <input type="text" value={formData.fm_title} onChange={(e) => setFormData({ ...formData, fm_title: e.target.value })} className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Subtitle</label>
                <input type="text" value={formData.fm_subtitle} onChange={(e) => setFormData({ ...formData, fm_subtitle: e.target.value })} className={inputClass} />
              </div>
            </div>
          </div>
        </div>

        {/* Gate (Entrance) Text */}
        <div className={sectionClass}>
          <h4 className={headingClass}>🚪 Souk Gate Text</h4>
          <div className="space-y-2">
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className={labelClass}>Gate Icon</label>
                <input type="text" value={formData.fm_gate_icon} onChange={(e) => setFormData({ ...formData, fm_gate_icon: e.target.value })} className={inputClass} placeholder="🏮" />
              </div>
              <div>
                <label className={labelClass}>Gate Title</label>
                <input type="text" value={formData.fm_gate_title} onChange={(e) => setFormData({ ...formData, fm_gate_title: e.target.value })} className={inputClass} placeholder="Bab el Souk" />
              </div>
              <div>
                <label className={labelClass}>Enter Button</label>
                <input type="text" value={formData.fm_enter_button} onChange={(e) => setFormData({ ...formData, fm_enter_button: e.target.value })} className={inputClass} placeholder="Enter Souk" />
              </div>
            </div>
            <div>
              <label className={labelClass}>Gate Subtitle</label>
              <input type="text" value={formData.fm_gate_subtitle} onChange={(e) => setFormData({ ...formData, fm_gate_subtitle: e.target.value })} className={inputClass} placeholder="{count} merchants await you" />
              <p className={hintClass}>Use {'{count}'} for number of merchants</p>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className={labelClass}>Exit Title</label>
                <input type="text" value={formData.fm_exit_title} onChange={(e) => setFormData({ ...formData, fm_exit_title: e.target.value })} className={inputClass} placeholder="End of Souk" />
              </div>
              <div>
                <label className={labelClass}>Exit Subtitle</label>
                <input type="text" value={formData.fm_exit_subtitle} onChange={(e) => setFormData({ ...formData, fm_exit_subtitle: e.target.value })} className={inputClass} placeholder="You've seen all the merchants!" />
              </div>
            </div>
          </div>
        </div>

        {/* Shopping Flow Text */}
        <div className={sectionClass}>
          <h4 className={headingClass}>💬 Shopping Flow — Merchant Phrases</h4>
          <p className={hintClass + ' mb-3'}>Use {'{category}'} as placeholder for the selected category name</p>
          <div className="space-y-3">
            {[
              { label: 'Greeting', dKey: 'fm_greeting_darija', eKey: 'fm_greeting_english' },
              { label: 'Category Prompt (English only)', dKey: null, eKey: 'fm_category_prompt' },
              { label: 'No Items in Category', dKey: 'fm_no_items_darija', eKey: 'fm_no_items_english' },
              { label: 'Show Items', dKey: 'fm_items_darija', eKey: 'fm_items_english' },
              { label: 'Ask Color', dKey: 'fm_color_darija', eKey: 'fm_color_english' },
              { label: 'Ask Size', dKey: 'fm_size_darija', eKey: 'fm_size_english' },
              { label: 'Ready to Deal', dKey: 'fm_deal_darija', eKey: 'fm_deal_english' },
              { label: 'After Purchase', dKey: 'fm_purchase_darija', eKey: 'fm_purchase_english' },
            ].map(({ label, dKey, eKey }) => (
              <div key={eKey} className={`p-2 rounded border ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}>
                <label className={`block text-xs font-bold mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{label}</label>
                <div className={dKey ? 'grid grid-cols-2 gap-2' : ''}>
                  {dKey && (
                    <input type="text" value={formData[dKey]} onChange={(e) => setFormData({ ...formData, [dKey]: e.target.value })} className={inputClass} placeholder="Darija" />
                  )}
                  <input type="text" value={formData[eKey]} onChange={(e) => setFormData({ ...formData, [eKey]: e.target.value })} className={inputClass} placeholder="English" />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Color Discovery Flow */}
        <div className={sectionClass}>
          <h4 className={headingClass}>🎨 Color Discovery Flow</h4>
          <p className={hintClass + ' mb-3'}>Use {'{color}'} as placeholder for the Darija color name</p>
          <div className="space-y-3">
            {[
              { label: 'Ask Color Preference', dKey: 'fm_color_ask_preference_darija', eKey: 'fm_color_ask_preference_english' },
              { label: 'Merchant HAS Color', dKey: 'fm_color_has_it_darija', eKey: 'fm_color_has_it_english' },
              { label: "Merchant DOESN'T Have Color", dKey: 'fm_color_no_has_darija', eKey: 'fm_color_no_has_english' },
              { label: 'Great Color Choice', dKey: 'fm_color_great_choice_darija', eKey: 'fm_color_great_choice_english' },
            ].map(({ label, dKey, eKey }) => (
              <div key={eKey} className={`p-2 rounded border ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}>
                <label className={`block text-xs font-bold mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{label}</label>
                <div className="grid grid-cols-2 gap-2">
                  <input type="text" value={formData[dKey]} onChange={(e) => setFormData({ ...formData, [dKey]: e.target.value })} className={inputClass} placeholder="Darija" />
                  <input type="text" value={formData[eKey]} onChange={(e) => setFormData({ ...formData, [eKey]: e.target.value })} className={inputClass} placeholder="English" />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Button Labels */}
        <div className={sectionClass}>
          <h4 className={headingClass}>🔘 Button Labels</h4>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className={labelClass}>Bargain Button</label>
              <input type="text" value={formData.fm_bargain_button} onChange={(e) => setFormData({ ...formData, fm_bargain_button: e.target.value })} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Buy Button</label>
              <input type="text" value={formData.fm_buy_button} onChange={(e) => setFormData({ ...formData, fm_buy_button: e.target.value })} className={inputClass} />
            </div>
          </div>
        </div>

        {/* Merchant Callout Phrases */}
        <div className={sectionClass}>
          <h4 className={headingClass}>📢 Merchant Callout Phrases (speech bubbles)</h4>
          <div className="space-y-2">
            {callouts.map((c, i) => (
              <div key={i} className="flex gap-2 items-start">
                <div className="flex-1 grid grid-cols-2 gap-2">
                  <input type="text" value={c.darija} onChange={(e) => { const arr = [...callouts]; arr[i] = { ...arr[i], darija: e.target.value }; setCallouts(arr); }} className={inputClass} placeholder="Darija" />
                  <input type="text" value={c.english} onChange={(e) => { const arr = [...callouts]; arr[i] = { ...arr[i], english: e.target.value }; setCallouts(arr); }} className={inputClass} placeholder="English" />
                </div>
                <button onClick={() => setCallouts(callouts.filter((_, j) => j !== i))} className="text-red-400 hover:text-red-600 text-sm px-1 pt-2">✕</button>
              </div>
            ))}
            <button onClick={() => setCallouts([...callouts, { darija: '', english: '' }])} className={`text-xs font-semibold px-3 py-1.5 rounded-lg ${isDarkMode ? 'bg-gray-700 text-teal-400 hover:bg-gray-600' : 'bg-teal-50 text-teal-700 hover:bg-teal-100'}`}>
              + Add Phrase
            </button>
          </div>
        </div>

        {/* Floating Menu Appearance */}
        <div className={sectionClass}>
          <p className={headingClass}>📱 Floating Menu Icons</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Mobile Size</label>
              <select value={formData.fm_menu_mobile_size} onChange={e => setFormData({ ...formData, fm_menu_mobile_size: e.target.value })} className={inputClass}>
                <option value="small">Small (32px)</option>
                <option value="medium">Medium (40px)</option>
                <option value="large">Large (48px)</option>
              </select>
            </div>
            <div>
              <label className={labelClass}>Desktop Size</label>
              <select value={formData.fm_menu_desktop_size} onChange={e => setFormData({ ...formData, fm_menu_desktop_size: e.target.value })} className={inputClass}>
                <option value="small">Small (36px)</option>
                <option value="medium">Medium (44px)</option>
                <option value="large">Large (56px)</option>
              </select>
            </div>
            <div>
              <label className={labelClass}>Spacing</label>
              <select value={formData.fm_menu_gap} onChange={e => setFormData({ ...formData, fm_menu_gap: e.target.value })} className={inputClass}>
                <option value="tight">Tight</option>
                <option value="normal">Normal</option>
                <option value="spacious">Spacious</option>
              </select>
            </div>
            <div>
              <label className={labelClass}>Vertical Position</label>
              <select value={formData.fm_menu_position} onChange={e => setFormData({ ...formData, fm_menu_position: e.target.value })} className={inputClass}>
                <option value="top">Top</option>
                <option value="upper-third">Upper Third</option>
                <option value="center">Center</option>
              </select>
            </div>
          </div>
          <p className={hintClass}>Controls the Cart, Closet, Wallet, Stats, Style, Help icons on the right side</p>
        </div>

        {/* Shopping Flow Buttons */}
        <details className={sectionClass}>
          <summary className={`${headingClass} cursor-pointer select-none`}>🛒 Shopping Flow Buttons</summary>
          <div className="space-y-2 mt-3">
            <p className={hintClass + ' mb-2'}>Use {'{item}'} for item name, {'{merchant}'} for merchant name</p>
            <div className="grid grid-cols-2 gap-2">
              {[
                { key: 'fm_closet_button', label: 'Closet Button' },
                { key: 'fm_back_to_souk_button', label: 'Back to Souk' },
                { key: 'fm_back_to_categories', label: 'Other Categories' },
                { key: 'fm_full_price_label', label: 'Full Price Label' },
                { key: 'fm_owned_warning', label: 'Already Owned' },
                { key: 'fm_no_tokens_warning', label: 'Not Enough Tokens' },
                { key: 'fm_item_purchased_title', label: 'Purchase Title' },
                { key: 'fm_word_learned_label', label: 'Word Learned' },
                { key: 'fm_added_to_collection', label: 'Added to Collection' },
                { key: 'fm_visit_another', label: 'Visit Another' },
                { key: 'fm_open_closet', label: 'Open Closet' },
                { key: 'fm_color_pick_label', label: 'Color Pick Label' },
                { key: 'fm_color_no_thanks', label: 'No Thanks (Color)' },
                { key: 'fm_color_continue', label: 'Continue to Buy' },
                { key: 'fm_color_try_another', label: 'Try Another Color' },
                { key: 'fm_color_keep_item', label: 'Keep Item' },
                { key: 'fm_keep_shopping', label: 'Keep Shopping' },
              ].map(({ key, label }) => (
                <div key={key}>
                  <label className={labelClass}>{label}</label>
                  <input type="text" value={formData[key]} onChange={(e) => setFormData({ ...formData, [key]: e.target.value })} className={inputClass} />
                </div>
              ))}
            </div>
          </div>
        </details>

        {/* Merchant Personality Labels & Tips */}
        <details className={sectionClass}>
          <summary className={`${headingClass} cursor-pointer select-none`}>🎭 Merchant Personality Labels</summary>
          <div className="space-y-2 mt-3">
            <p className={hintClass + ' mb-2'}>Labels & tips shown on merchant personality badges</p>
            <div className="grid grid-cols-3 gap-1 mb-1">
              <span className={`text-xs font-bold ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Type</span>
              <span className={`text-xs font-bold ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Label</span>
              <span className={`text-xs font-bold ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Tip</span>
            </div>
            {Object.keys(personalityLabels).map(key => (
              <div key={key} className="grid grid-cols-3 gap-2 items-center">
                <span className={`text-xs font-medium capitalize ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{key}</span>
                <input type="text" value={personalityLabels[key]} onChange={(e) => setPersonalityLabels({ ...personalityLabels, [key]: e.target.value })} className={inputClass} placeholder="Label" />
                <input type="text" value={personalityTips[key] || ''} onChange={(e) => setPersonalityTips({ ...personalityTips, [key]: e.target.value })} className={inputClass} placeholder="Tip" />
              </div>
            ))}
          </div>
        </details>

        {/* Darija Size Translations */}
        <details className={sectionClass}>
          <summary className={`${headingClass} cursor-pointer select-none`}>📏 Darija Size Translations</summary>
          <div className="grid grid-cols-3 gap-2 mt-3">
            {Object.keys(sizeTranslations).map(size => (
              <div key={size}>
                <label className={labelClass}>{size}</label>
                <input type="text" value={sizeTranslations[size]} onChange={(e) => setSizeTranslations({ ...sizeTranslations, [size]: e.target.value })} className={inputClass} />
              </div>
            ))}
          </div>
        </details>

        {/* Game Icons */}
        <details className={sectionClass}>
          <summary className={`${headingClass} cursor-pointer select-none`}>🎨 Game Icons</summary>
          <div className="space-y-4 mt-3">
            {[
              { group: 'Menu Icons', items: [
                ['icon_cart_menu', 'Cart button'], ['icon_closet_menu', 'Closet button'], ['icon_wallet_menu', 'Wallet button'],
                ['icon_stats_menu', 'Stats button'], ['icon_style_menu', 'Style/Avatar button'], ['icon_help_menu', 'Help button'],
              ]},
              { group: 'Currency & Economy', items: [
                ['icon_token', 'Token currency (🪙)'], ['icon_streak', 'Login streak fire'], ['icon_gift', 'Daily reward gift'],
              ]},
              { group: 'Clothing Categories', items: [
                ['icon_headwear', 'Headwear category'], ['icon_tops', 'Tops category'], ['icon_outerwear', 'Outerwear category'],
                ['icon_bottoms', 'Bottoms category'], ['icon_shoes', 'Shoes category'], ['icon_accessories', 'Accessories category'],
              ]},
              { group: 'Map Areas', items: [
                ['icon_area_main_alley', 'Main Alley'], ['icon_area_side_alley', 'Side Alley'], ['icon_area_hidden_alley', 'Hidden Alley'],
                ['icon_area_night_souk', 'Night Souk'], ['icon_area_rooftop', 'Rooftop Market'],
              ]},
              { group: 'Mission Occasions', items: [
                ['icon_occasion_wedding', 'Wedding'], ['icon_occasion_beach', 'Beach'], ['icon_occasion_winter', 'Winter'],
                ['icon_occasion_school', 'School'], ['icon_occasion_work', 'Work'], ['icon_occasion_casual', 'Casual'],
                ['icon_occasion_traditional', 'Traditional'], ['icon_occasion_party', 'Party'], ['icon_occasion_eid', 'Eid'],
                ['icon_occasion_formal', 'Formal'], ['icon_occasion_sport', 'Sport'], ['icon_occasion_date', 'Date'],
              ]},
              { group: 'UI Defaults', items: [
                ['icon_default_merchant', 'Default merchant face'], ['icon_default_item', 'Default item icon'], ['icon_empty_state', 'Empty state icon'],
                ['icon_locked', 'Locked area'], ['icon_walking', 'Walking animation'], ['icon_trade', 'Trade/swap icon'],
              ]},
              { group: 'Dashboard Stats', items: [
                ['icon_dashboard_stars', 'Stars stat'], ['icon_dashboard_saved', 'Saved stat'], ['icon_dashboard_items', 'Items stat'], ['icon_dashboard_streak', 'Streak stat'],
              ]},
            ].map(({ group, items }) => (
              <div key={group}>
                <p className={`text-xs font-bold mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{group}</p>
                <div className="grid grid-cols-3 gap-2">
                  {items.map(([key, desc]) => {
                    const val = formData[key] || '';
                    const isImage = val.startsWith('/') || val.startsWith('http');
                    return (
                      <div key={key} className="flex items-center gap-1.5">
                        {isImage ? (
                          <div className="w-[50px] h-[36px] rounded border border-gray-300 flex items-center justify-center bg-gray-50 overflow-hidden flex-shrink-0">
                            <img src={val.startsWith('http') ? val : `${API.replace('/api', '')}${val}`} alt="" className="max-w-full max-h-full object-contain" />
                          </div>
                        ) : (
                          <input type="text" value={val} onChange={e => setFormData({...formData, [key]: e.target.value})} className={inputClass} style={{maxWidth: 50, textAlign: 'center', fontSize: '1.1rem'}} />
                        )}
                        <button
                          type="button"
                          onClick={() => setIconPickerField(key)}
                          className={`w-7 h-7 rounded flex items-center justify-center text-xs flex-shrink-0 ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-600'}`}
                          title="Pick from Image Manager"
                        >🖼️</button>
                        <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} truncate`}>{desc}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
          {iconPickerField && (
            <IconPicker
              API={API}
              adminToken={adminToken}
              isDarkMode={isDarkMode}
              onClose={() => setIconPickerField(null)}
              onPick={(url) => {
                setFormData({...formData, [iconPickerField]: url});
                setIconPickerField(null);
              }}
            />
          )}
        </details>

        {/* Save button */}
        <button onClick={handleSave} className="w-full py-2.5 rounded-lg bg-teal-600 hover:bg-teal-700 text-white font-bold text-sm transition-colors">
          Save Free Market Settings
        </button>
      </div>
    </div>
  );
};

const SettingsEditor = ({ settings, onSave, isDarkMode }) => {
  const [formData, setFormData] = useState({ ...settings });
  const [howToPlay, setHowToPlay] = useState(settings.how_to_play || [
    { title: 'Choose a mission', description: 'each has a budget, time limit, and required items' },
    { title: 'Explore the souk', description: 'walk between areas and discover different merchants' },
    { title: 'Bargain in Darija', description: 'use Darija phrases to negotiate prices with merchants' },
    { title: 'Earn 3 stars', description: 'find all items, stay under budget, and use Darija' }
  ]);

  const [bargainPhrases, setBargainPhrases] = useState(settings.bargain_phrases || [
    { id: 'ghalia', darija: 'Ghalia bzaf!', english: 'Too expensive!', icon: '😮', effect: 'hard', moodCost: 'high', desc: 'Big price drop, but annoys merchant' },
    { id: 'zwin', darija: 'Zwin bzaf!', english: 'Very beautiful!', icon: '😍', effect: 'easy', moodCost: 'low', desc: 'Small drop, merchant stays happy' },
    { id: 'rkhis', darija: "3ndek shi 7aja rkhisa?", english: 'Something cheaper?', icon: '🔍', effect: 'medium', moodCost: 'medium', desc: 'Moderate drop, slight mood cost' },
    { id: 'safi', darija: 'Safi, makaynsh', english: 'OK, no thanks (walk away)', icon: '🚶', effect: 'walkaway', moodCost: 'none', desc: 'Risky! 50/50 best price or lose item' },
  ]);
  const [bargainMoodLabels, setBargainMoodLabels] = useState(settings.bargain_mood_labels || { happy: 'Happy', neutral: 'Neutral', annoyed: 'Annoyed' });
  const [walletTips, setWalletTips] = useState(settings.wallet_tips || [
    'Earn tokens by completing missions',
    'Save tokens by bargaining with merchants',
    'Daily login rewards increase with streaks'
  ]);
  const [bargainBadges, setBargainBadges] = useState(settings.dashboard_bargain_badges || [
    { threshold: 100, name: 'Smart Saver', emoji: '💵', desc: 'Save 100 Tokens' },
    { threshold: 500, name: 'Negotiator', emoji: '💸', desc: 'Save 500 Tokens' },
    { threshold: 1000, name: 'Bargain Pro', emoji: '💎', desc: 'Save 1K Tokens' },
    { threshold: 5000, name: 'Legendary', emoji: '👑', desc: 'Save 5K Tokens' },
  ]);
  const [collectorBadges, setCollectorBadges] = useState(settings.dashboard_collector_badges || [
    { threshold: 10, name: 'First Steps', emoji: '🛍️', desc: 'Own 10 items' },
    { threshold: 25, name: 'Explorer', emoji: '👔', desc: 'Own 25 items' },
    { threshold: 50, name: 'Fashion Lover', emoji: '💃', desc: 'Own 50 items' },
    { threshold: 100, name: 'Wardrobe Master', emoji: '🏪', desc: 'Own 100 items' },
  ]);

  const handleSave = () => {
    onSave({ ...formData, how_to_play: howToPlay, bargain_phrases: bargainPhrases, bargain_mood_labels: bargainMoodLabels, wallet_tips: walletTips, dashboard_bargain_badges: bargainBadges, dashboard_collector_badges: collectorBadges });
  };

  return (
    <div className={`rounded-lg p-4 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border`}>
      <h3 className={`text-lg font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>🎯 Missions Settings</h3>
      
      <div className="space-y-3">
        {/* Toggles Row */}
        <div className="grid grid-cols-3 gap-2">
          <div className="flex items-center justify-between p-2 rounded border" style={{borderColor: isDarkMode ? '#374151' : '#e5e7eb'}}>
            <label className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>👁️ Visible</label>
            <input
              type="checkbox"
              checked={formData.missions_is_active !== false}
              onChange={(e) => setFormData({...formData, missions_is_active: e.target.checked})}
              className="w-5 h-5"
            />
          </div>
          <div className="flex items-center justify-between p-2 rounded border" style={{borderColor: isDarkMode ? '#374151' : '#e5e7eb'}}>
            <label className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>🔒 Pro</label>
            <input
              type="checkbox"
              checked={formData.missions_is_pro_only || false}
              onChange={(e) => setFormData({...formData, missions_is_pro_only: e.target.checked})}
              className="w-5 h-5"
            />
          </div>
          <div className="flex items-center justify-between p-2 rounded border" style={{borderColor: isDarkMode ? '#374151' : '#e5e7eb'}}>
            <label className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>💰 Buy</label>
            <input
              type="checkbox"
              checked={formData.show_buy_coins}
              onChange={(e) => setFormData({...formData, show_buy_coins: e.target.checked})}
              className="w-5 h-5"
            />
          </div>
        </div>

        {/* Starting Tokens Control */}
        <div>
          <label className={`block font-medium mb-1 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>💰 Default Starting Tokens</label>
          <input
            type="number"
            value={formData.starting_tokens || formData.starting_coins || 5000}
            onChange={(e) => setFormData({...formData, starting_tokens: parseInt(e.target.value)})}
            className={`w-full px-3 py-2 rounded-lg border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
            min="0"
            placeholder="5000"
          />
          <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            New users will start with this amount of Tokens (game currency)
          </p>
        </div>

        {/* Game Defaults */}
        <div className={`p-3 rounded-lg border ${isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-teal-50 border-teal-200'}`}>
          <h4 className={`font-bold mb-2 text-sm ${isDarkMode ? 'text-teal-300' : 'text-teal-800'}`}>🎮 Game Defaults</h4>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className={`block text-xs mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Default Time Limit (sec)</label>
              <input
                type="number"
                value={formData.default_time_limit || 0}
                onChange={(e) => setFormData({...formData, default_time_limit: parseInt(e.target.value)})}
                min="0"
                className={`w-full px-2 py-1 rounded border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
              />
              <p className={`text-xs mt-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>0 = no timer</p>
            </div>
            <div>
              <label className={`block text-xs mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Default Budget</label>
              <input
                type="number"
                value={formData.default_budget || 0}
                onChange={(e) => setFormData({...formData, default_budget: parseInt(e.target.value)})}
                min="0"
                className={`w-full px-2 py-1 rounded border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
              />
              <p className={`text-xs mt-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>0 = unlimited</p>
            </div>
            <div>
              <label className={`block text-xs mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Walking Time Cost (sec)</label>
              <input
                type="number"
                value={formData.walking_time_cost || 30}
                onChange={(e) => setFormData({...formData, walking_time_cost: parseInt(e.target.value)})}
                min="0"
                className={`w-full px-2 py-1 rounded border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
              />
              <p className={`text-xs mt-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Time deducted when walking between areas</p>
            </div>
          </div>
        </div>

        {/* Welcome Screen Content */}
        <div className={`p-3 rounded-lg border ${isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-orange-50 border-orange-200'}`}>
          <h4 className={`font-bold mb-2 text-sm ${isDarkMode ? 'text-orange-300' : 'text-orange-800'}`}>🏪 Welcome Screen Content</h4>
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className={`block text-xs mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Icon (Emoji)</label>
                <input
                  type="text"
                  value={formData.welcome_icon || '🏪'}
                  onChange={(e) => setFormData({...formData, welcome_icon: e.target.value})}
                  className={`w-full px-2 py-1 rounded border text-sm text-center ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                  placeholder="🏪"
                />
              </div>
              <div>
                <label className={`block text-xs mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Title</label>
                <input
                  type="text"
                  value={formData.welcome_title || 'Souk L7wayej'}
                  onChange={(e) => setFormData({...formData, welcome_title: e.target.value})}
                  className={`w-full px-2 py-1 rounded border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                />
              </div>
            </div>
            <div>
              <label className={`block text-xs mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Subtitle</label>
              <input
                type="text"
                value={formData.welcome_subtitle || 'The Moroccan Clothes Market Adventure'}
                onChange={(e) => setFormData({...formData, welcome_subtitle: e.target.value})}
                className={`w-full px-2 py-1 rounded border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
              />
            </div>
            <div>
              <label className={`block text-xs mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Disclaimer</label>
              <textarea
                value={formData.welcome_disclaimer || 'This is an educational game for learning Moroccan Darija. All merchants, products, and prices are simulated for learning purposes.'}
                onChange={(e) => setFormData({...formData, welcome_disclaimer: e.target.value})}
                rows={2}
                className={`w-full px-2 py-1 rounded border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
              />
            </div>
            {/* Additional Welcome Screen Fields */}
            <div className="grid grid-cols-2 gap-2 mt-2">
              {[
                { key: 'welcome_back_button', label: 'Back to Games Button', d: 'Back to Games' },
                { key: 'welcome_how_to_play_heading', label: 'How to Play Heading', d: 'How to Play' },
                { key: 'welcome_stats_stars_label', label: 'Stats: Stars', d: 'Stars' },
                { key: 'welcome_stats_tokens_label', label: 'Stats: Tokens', d: 'Tokens' },
                { key: 'welcome_stats_missions_label', label: 'Stats: Missions', d: 'Missions' },
                { key: 'welcome_fm_button', label: 'Free Market Button', d: 'Free Market' },
                { key: 'welcome_fm_desc', label: 'Free Market Desc', d: 'Shop, trade & dress up' },
                { key: 'welcome_missions_button', label: 'Missions Button', d: 'Missions' },
                { key: 'welcome_missions_desc', label: 'Missions Desc', d: 'Complete outfit challenges' },
                { key: 'welcome_loading_text', label: 'Loading Text', d: 'Loading the Souk...' },
              ].map(({ key, label, d }) => (
                <div key={key}>
                  <label className={`block text-xs mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{label}</label>
                  <input type="text" value={formData[key] ?? ''} onChange={(e) => setFormData({...formData, [key]: e.target.value})} className={`w-full px-2 py-1 rounded border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`} placeholder={d} />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* How to Play Steps */}
        <div className={`p-3 rounded-lg border ${isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-green-50 border-green-200'}`}>
          <div className="flex items-center justify-between mb-2">
            <h4 className={`font-bold text-sm ${isDarkMode ? 'text-green-300' : 'text-green-800'}`}>📋 How to Play Steps</h4>
            <button
              onClick={() => setHowToPlay([...howToPlay, { title: '', description: '' }])}
              className="text-xs bg-green-600 hover:bg-green-700 text-white px-2 py-0.5 rounded font-medium"
            >
              + Add Step
            </button>
          </div>
          <div className="space-y-2">
            {howToPlay.map((step, i) => (
              <div key={i} className={`flex gap-2 items-start p-2 rounded border ${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'}`}>
                <span className={`text-xs font-bold mt-1.5 flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-white ${
                  ['bg-[#C84B31]', 'bg-[#D4A843]', 'bg-[#2D6A4F]', 'bg-amber-500', 'bg-blue-500', 'bg-purple-500'][i % 6]
                }`}>{i + 1}</span>
                <div className="flex-1 space-y-1">
                  <input
                    type="text"
                    value={step.title}
                    onChange={(e) => {
                      const updated = [...howToPlay];
                      updated[i] = { ...updated[i], title: e.target.value };
                      setHowToPlay(updated);
                    }}
                    placeholder="Step title"
                    className={`w-full px-2 py-1 rounded border text-xs font-semibold ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-gray-50 border-gray-200'}`}
                  />
                  <input
                    type="text"
                    value={step.description}
                    onChange={(e) => {
                      const updated = [...howToPlay];
                      updated[i] = { ...updated[i], description: e.target.value };
                      setHowToPlay(updated);
                    }}
                    placeholder="Step description"
                    className={`w-full px-2 py-1 rounded border text-xs ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-gray-50 border-gray-200'}`}
                  />
                </div>
                {howToPlay.length > 1 && (
                  <button
                    onClick={() => setHowToPlay(howToPlay.filter((_, j) => j !== i))}
                    className="text-red-400 hover:text-red-600 text-sm mt-1 flex-shrink-0"
                  >✕</button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* ========== ALL GAME TEXT SECTIONS ========== */}
        {(() => {
          const inputClass = `w-full px-2 py-1 rounded border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`;
          const labelClass = `block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`;
          const secClass = `p-3 rounded-lg border ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`;
          const hdClass = `font-bold text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`;
          const hintCls = `text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`;
          const addBtnClass = `text-xs font-semibold px-3 py-1.5 rounded-lg ${isDarkMode ? 'bg-gray-700 text-teal-400 hover:bg-gray-600' : 'bg-teal-50 text-teal-700 hover:bg-teal-100'}`;

          const textSections = [
            { title: 'Upgrade Modal', icon: '🔒', fields: [
              { key: 'upgrade_title', label: 'Title', d: 'Pro Feature' },
              { key: 'upgrade_message', label: 'Message', d: 'This game mode is exclusive to Pro members. Upgrade to unlock all missions and features!' },
              { key: 'upgrade_button', label: 'Upgrade Button', d: 'Upgrade to Pro' },
              { key: 'upgrade_cancel', label: 'Cancel Button', d: 'Maybe Later' },
            ]},
            { title: 'Mission Select', icon: '🎯', fields: [
              { key: 'mission_select_title', label: 'Title', d: 'Choose Your Mission' },
              { key: 'mission_select_subtitle', label: 'Subtitle', d: 'Complete outfit challenges in the souk' },
              { key: 'mission_exit_button', label: 'Exit Button', d: 'Exit' },
              { key: 'mission_total_stars_label', label: 'Total Stars Label', d: 'total stars' },
              { key: 'mission_completed_badge', label: 'Completed Badge', d: 'Completed' },
              { key: 'mission_default_desc', label: 'Default Description', d: 'Complete this outfit challenge' },
              { key: 'mission_empty_title', label: 'Empty Title', d: 'No missions available yet' },
              { key: 'mission_empty_desc', label: 'Empty Description', d: 'Check back soon!' },
            ]},
            { title: 'Mission Complete', icon: '🏆', fields: [
              { key: 'mission_complete_perfect', label: 'Perfect Score', d: 'Perfect!' },
              { key: 'mission_complete_great', label: 'Great Score', d: 'Great Job!' },
              { key: 'mission_complete_done', label: 'Done Label', d: 'Mission Complete' },
              { key: 'mission_complete_all_items', label: 'All Items Found', d: 'All items found' },
              { key: 'mission_complete_under_budget', label: 'Under Budget', d: 'Under budget' },
              { key: 'mission_complete_used_darija', label: 'Used Darija', d: 'Used Darija phrases' },
              { key: 'mission_complete_reward', label: 'Reward Label', d: 'Reward earned' },
              { key: 'mission_complete_new_best', label: 'New Best', d: 'New personal best!' },
              { key: 'mission_complete_area_unlocked', label: 'Area Unlocked', d: 'New Area Unlocked!' },
              { key: 'mission_complete_replay', label: 'Replay Button', d: 'Replay for 3★' },
              { key: 'mission_complete_next', label: 'Next Button', d: 'Next Mission →' },
              { key: 'mission_complete_back', label: 'Back Button', d: 'Back to Missions' },
            ]},
            { title: 'Story Intro', icon: '📖', hint: 'Use {N} for number placeholder', fields: [
              { key: 'story_budget_label', label: 'Budget Label', d: 'Budget' },
              { key: 'story_no_limit', label: 'No Limit', d: 'No limit' },
              { key: 'story_time_label', label: 'Time Label', d: 'Time' },
              { key: 'story_items_label', label: 'Items Label', d: 'Items' },
              { key: 'story_earn_stars', label: 'Earn Stars Heading', d: 'Earn 3 Stars:' },
              { key: 'story_star_items', label: 'Star: Find Items', d: 'Find all required items' },
              { key: 'story_star_budget', label: 'Star: Budget', d: 'Stay under budget' },
              { key: 'story_star_darija', label: 'Star: Darija', d: 'Use {N} Darija phrases while bargaining' },
              { key: 'story_back_button', label: 'Back Button', d: 'Back' },
              { key: 'story_enter_button', label: 'Enter Button', d: 'Enter the Souk →' },
              { key: 'story_disclaimer', label: 'Disclaimer', d: 'Educational game — learn Darija bargaining phrases' },
            ]},
            { title: 'Bargain Game', icon: '🤝', hint: 'Use {price} for token amount, {N} for count', fields: [
              { key: 'bargain_final_price', label: 'Final Price', d: 'Final price — no more bargaining!' },
              { key: 'bargain_deal_button', label: 'Deal Button', d: 'Deal! Buy at 🪙 {price}' },
              { key: 'bargain_leave_button', label: 'Leave Button', d: 'Leave' },
              { key: 'bargain_use_phrase', label: 'Use Phrase', d: 'Use a Darija phrase:' },
              { key: 'bargain_attempts_left', label: 'Attempts Left', d: '{N} attempts left' },
              { key: 'bargain_deal_text', label: 'Deal Text', d: 'Wakha! Deal at 🪙 {price}' },
              { key: 'bargain_walked_away', label: 'Walked Away', d: 'The merchant let you walk away...' },
              { key: 'bargain_too_angry', label: 'Too Angry', d: 'The merchant is too angry to negotiate!' },
              { key: 'bargain_success_title', label: 'Success Title', d: 'Deal!' },
              { key: 'bargain_saved_text', label: 'Saved Text', d: 'You saved {discount}% ({saved} Tokens)' },
            ]},
            { title: 'Cart', icon: '🛒', hint: 'Use {N} for number, {total} for total tokens', fields: [
              { key: 'cart_title', label: 'Title', d: 'Your Cart' },
              { key: 'cart_items_label', label: 'Items Label', d: 'items' },
              { key: 'cart_empty_title', label: 'Empty Title', d: 'Your cart is empty' },
              { key: 'cart_empty_desc', label: 'Empty Desc', d: 'Visit merchants and add items' },
              { key: 'cart_budget_label', label: 'Budget Label', d: 'Budget' },
              { key: 'cart_over_budget', label: 'Over Budget', d: "Over budget! You'll lose a star." },
              { key: 'cart_total_label', label: 'Total Label', d: 'Total' },
              { key: 'cart_balance_label', label: 'Balance Label', d: 'Your balance' },
              { key: 'cart_not_enough_tokens', label: 'Not Enough', d: 'Not enough tokens! You need {N} more.' },
              { key: 'cart_checkout_button', label: 'Checkout Button', d: 'Checkout — 🪙 {total}' },
              { key: 'cart_not_enough_button', label: 'Not Enough Button', d: 'Not enough tokens' },
            ]},
            { title: 'Souk Map', icon: '🗺️', hint: 'Use {N}/{total} for counts', fields: [
              { key: 'map_title', label: 'Title', d: 'Souk Map' },
              { key: 'map_areas_unlocked', label: 'Areas Unlocked', d: '{N}/{total} areas unlocked' },
              { key: 'map_items_still_needed', label: 'Items Needed', d: '{N} items still needed' },
              { key: 'map_all_items_found', label: 'All Found', d: 'All items found!' },
              { key: 'map_walking', label: 'Walking', d: 'Walking through the souk...' },
              { key: 'map_walking_cost', label: 'Walk Cost', d: '-{N} seconds from your time' },
              { key: 'map_items_you_need', label: 'Items You Need', d: 'Items you need here:' },
              { key: 'map_merchants_label', label: 'Merchants Label', d: 'Merchants' },
              { key: 'map_missions_label', label: 'Missions Label', d: 'missions' },
              { key: 'map_visit_button', label: 'Visit Button', d: 'Visit' },
              { key: 'map_no_merchants', label: 'No Merchants', d: 'No merchants in this area yet' },
              { key: 'map_walking_hint', label: 'Walking Hint', d: 'Walking between areas costs {N} seconds — choose wisely!' },
            ]},
            { title: 'Merchant Shop', icon: '🏪', fields: [
              { key: 'shop_back_button', label: 'Back Button', d: 'Back to Map' },
              { key: 'shop_default_greeting', label: 'Default Greeting', d: 'Marhba! Welcome!' },
              { key: 'shop_budget_label', label: 'Budget Label', d: 'Budget' },
              { key: 'shop_over_budget', label: 'Over Budget', d: 'Over budget! Remove items from cart or bargain.' },
              { key: 'shop_all_items', label: 'All Items Tab', d: 'All Items' },
              { key: 'shop_needed_badge', label: 'Needed Badge', d: 'NEEDED' },
              { key: 'shop_rare_badge', label: 'Rare Badge', d: 'RARE' },
              { key: 'shop_traditional_badge', label: 'Traditional', d: 'Traditional' },
              { key: 'shop_owned_badge', label: 'Owned Badge', d: 'OWNED' },
              { key: 'shop_over_budget_badge', label: 'Over Budget Badge', d: 'OVER BUDGET' },
              { key: 'shop_already_owned', label: 'Already Owned', d: 'Already owned' },
              { key: 'shop_in_cart', label: 'In Cart', d: 'In cart ✓' },
              { key: 'shop_price_button', label: 'Price Button', d: 'Bsh7al? 💬' },
              { key: 'shop_bargain_button', label: 'Bargain', d: 'Bargain!' },
              { key: 'shop_buy_button', label: 'Buy', d: 'Buy' },
              { key: 'shop_empty_category', label: 'Empty Category', d: 'No items in this category' },
            ]},
            { title: 'Closet', icon: '👗', fields: [
              { key: 'closet_title', label: 'Title', d: 'My Closet' },
              { key: 'closet_pick_title', label: 'Pick Title', d: 'Pick an Item' },
              { key: 'closet_options_title', label: 'Options Title', d: 'Item Options' },
              { key: 'closet_pick_new', label: 'Pick New', d: 'Pick New Item' },
              { key: 'closet_confirm_trade', label: 'Confirm Trade', d: 'Confirm Trade' },
              { key: 'closet_outfits_label', label: 'Outfits Label', d: 'Outfits' },
              { key: 'closet_accessories_label', label: 'Accessories Label', d: 'Accessories' },
              { key: 'closet_dressup_button', label: 'Dress Up Button', d: 'Dress Up Avatar' },
              { key: 'closet_close_button', label: 'Close Button', d: 'Close' },
              { key: 'closet_empty_title', label: 'Empty Title', d: 'No items available to add' },
              { key: 'closet_empty_desc', label: 'Empty Desc', d: 'Buy items from merchants first!' },
              { key: 'closet_trade_button', label: 'Trade Button', d: 'Trade for Something New' },
              { key: 'closet_remove_button', label: 'Remove Button', d: 'Remove from Closet' },
              { key: 'closet_trade_credit', label: 'Trade Credit', d: 'Trade credit (50%)' },
              { key: 'closet_new_item_price', label: 'New Item Price', d: 'New item price' },
              { key: 'closet_you_pay', label: 'You Pay', d: 'You pay' },
              { key: 'closet_you_get_back', label: 'You Get Back', d: 'You get back' },
              { key: 'closet_even_swap', label: 'Even Swap', d: 'Even swap' },
              { key: 'closet_confirm_button', label: 'Confirm', d: 'Confirm Trade' },
              { key: 'closet_pick_different', label: 'Pick Different', d: 'Pick Different Item' },
              { key: 'closet_give_label', label: 'Give Label', d: 'GIVE' },
              { key: 'closet_get_label', label: 'Get Label', d: 'GET' },
            ]},
            { title: 'Wallet', icon: '💰', fields: [
              { key: 'wallet_title', label: 'Title', d: 'Wallet' },
              { key: 'wallet_balance_label', label: 'Balance Label', d: 'Your Balance' },
              { key: 'wallet_tokens_label', label: 'Tokens Label', d: 'Tokens' },
              { key: 'wallet_streak_label', label: 'Streak Label', d: 'Login Streak' },
              { key: 'wallet_days_row', label: 'Days in a Row', d: 'days in a row' },
              { key: 'wallet_daily_button', label: 'Daily Reward', d: 'Claim Daily Reward' },
              { key: 'wallet_claimed_msg', label: 'Claimed Message', d: 'Daily reward claimed — come back tomorrow!' },
            ]},
            { title: 'Dashboard', icon: '📊', fields: [
              { key: 'dashboard_title', label: 'Title', d: 'Dashboard' },
              { key: 'dashboard_subtitle', label: 'Subtitle', d: 'Stats, Progress & Achievements' },
              { key: 'dashboard_stars', label: 'Stars Label', d: 'Stars' },
              { key: 'dashboard_saved', label: 'Saved Label', d: 'Saved' },
              { key: 'dashboard_items', label: 'Items Label', d: 'Items' },
              { key: 'dashboard_streak', label: 'Streak Label', d: 'Streak' },
              { key: 'dashboard_missions_completed', label: 'Missions Label', d: 'Missions completed' },
              { key: 'dashboard_bargain_title', label: 'Bargain Title', d: 'Bargaining Master' },
              { key: 'dashboard_collector_title', label: 'Collector Title', d: 'Collector' },
            ]},
            { title: 'Top Bar & Menu', icon: '📱', fields: [
              { key: 'topbar_back_button', label: 'Back Button', d: 'Back' },
              { key: 'menu_cart', label: 'Menu: Cart', d: 'Cart' },
              { key: 'menu_closet', label: 'Menu: Closet', d: 'Closet' },
              { key: 'menu_wallet', label: 'Menu: Wallet', d: 'Wallet' },
              { key: 'menu_stats', label: 'Menu: Stats', d: 'Stats' },
              { key: 'menu_style', label: 'Menu: Style', d: 'Style' },
              { key: 'menu_help', label: 'Menu: Help', d: 'Help' },
            ]},
          ];

          return (
            <>
              {textSections.map(section => (
                <details key={section.title} className={secClass}>
                  <summary className={`${hdClass} cursor-pointer select-none`}>{section.icon} {section.title}</summary>
                  {section.hint && <p className={hintCls}>{section.hint}</p>}
                  <div className="grid grid-cols-2 gap-2 mt-3">
                    {section.fields.map(({ key, label, d }) => (
                      <div key={key}>
                        <label className={labelClass}>{label}</label>
                        <input type="text" value={formData[key] ?? ''} onChange={(e) => setFormData({...formData, [key]: e.target.value})} className={inputClass} placeholder={d} />
                      </div>
                    ))}
                  </div>
                </details>
              ))}

              {/* Bargain Mood Labels */}
              <details className={secClass}>
                <summary className={`${hdClass} cursor-pointer select-none`}>😊 Bargain Mood Labels</summary>
                <div className="grid grid-cols-3 gap-2 mt-3">
                  {Object.keys(bargainMoodLabels).map(key => (
                    <div key={key}>
                      <label className={labelClass + ' capitalize'}>{key}</label>
                      <input type="text" value={bargainMoodLabels[key]} onChange={(e) => setBargainMoodLabels({...bargainMoodLabels, [key]: e.target.value})} className={inputClass} />
                    </div>
                  ))}
                </div>
              </details>

              {/* Bargain Phrases */}
              <details className={secClass}>
                <summary className={`${hdClass} cursor-pointer select-none`}>🗣️ Bargain Phrases</summary>
                <div className="space-y-2 mt-3">
                  {bargainPhrases.map((p, i) => (
                    <div key={i} className={`p-2 rounded border ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                      <div className="grid grid-cols-2 gap-2 mb-1">
                        <input type="text" value={p.darija} onChange={(e) => { const arr = [...bargainPhrases]; arr[i] = {...arr[i], darija: e.target.value}; setBargainPhrases(arr); }} className={inputClass} placeholder="Darija" />
                        <input type="text" value={p.english} onChange={(e) => { const arr = [...bargainPhrases]; arr[i] = {...arr[i], english: e.target.value}; setBargainPhrases(arr); }} className={inputClass} placeholder="English" />
                      </div>
                      <div className="grid grid-cols-4 gap-1">
                        <input type="text" value={p.icon || ''} onChange={(e) => { const arr = [...bargainPhrases]; arr[i] = {...arr[i], icon: e.target.value}; setBargainPhrases(arr); }} className={inputClass} placeholder="Icon" />
                        <select value={p.effect || 'easy'} onChange={(e) => { const arr = [...bargainPhrases]; arr[i] = {...arr[i], effect: e.target.value}; setBargainPhrases(arr); }} className={inputClass}>
                          <option value="easy">Easy drop</option><option value="medium">Medium</option><option value="hard">Hard drop</option><option value="walkaway">Walk away</option>
                        </select>
                        <select value={p.moodCost || 'low'} onChange={(e) => { const arr = [...bargainPhrases]; arr[i] = {...arr[i], moodCost: e.target.value}; setBargainPhrases(arr); }} className={inputClass}>
                          <option value="none">No cost</option><option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option>
                        </select>
                        <button onClick={() => setBargainPhrases(bargainPhrases.filter((_, j) => j !== i))} className="text-red-400 hover:text-red-600 text-sm">✕</button>
                      </div>
                      <input type="text" value={p.desc || ''} onChange={(e) => { const arr = [...bargainPhrases]; arr[i] = {...arr[i], desc: e.target.value}; setBargainPhrases(arr); }} className={inputClass + ' mt-1'} placeholder="Description for player" />
                    </div>
                  ))}
                  <button onClick={() => setBargainPhrases([...bargainPhrases, { id: `custom_${Date.now()}`, darija: '', english: '', icon: '💬', effect: 'easy', moodCost: 'low', desc: '' }])} className={addBtnClass}>+ Add Phrase</button>
                </div>
              </details>

              {/* Wallet Tips */}
              <details className={secClass}>
                <summary className={`${hdClass} cursor-pointer select-none`}>💡 Wallet Tips</summary>
                <div className="space-y-2 mt-3">
                  {walletTips.map((tip, i) => (
                    <div key={i} className="flex gap-2">
                      <input type="text" value={tip} onChange={(e) => { const arr = [...walletTips]; arr[i] = e.target.value; setWalletTips(arr); }} className={inputClass + ' flex-1'} placeholder="Tip text" />
                      <button onClick={() => setWalletTips(walletTips.filter((_, j) => j !== i))} className="text-red-400 hover:text-red-600 text-sm">✕</button>
                    </div>
                  ))}
                  <button onClick={() => setWalletTips([...walletTips, ''])} className={addBtnClass}>+ Add Tip</button>
                </div>
              </details>

              {/* Dashboard Badges */}
              <details className={secClass}>
                <summary className={`${hdClass} cursor-pointer select-none`}>🏅 Dashboard Badges</summary>
                <div className="space-y-3 mt-3">
                  <p className={`${hdClass} text-xs`}>Bargaining Master Badges</p>
                  {bargainBadges.map((b, i) => (
                    <div key={i} className="grid grid-cols-5 gap-1 items-center">
                      <input type="number" value={b.threshold} onChange={(e) => { const arr = [...bargainBadges]; arr[i] = {...arr[i], threshold: parseInt(e.target.value) || 0}; setBargainBadges(arr); }} className={inputClass} placeholder="Threshold" />
                      <input type="text" value={b.emoji} onChange={(e) => { const arr = [...bargainBadges]; arr[i] = {...arr[i], emoji: e.target.value}; setBargainBadges(arr); }} className={inputClass} placeholder="Emoji" />
                      <input type="text" value={b.name} onChange={(e) => { const arr = [...bargainBadges]; arr[i] = {...arr[i], name: e.target.value}; setBargainBadges(arr); }} className={inputClass} placeholder="Name" />
                      <input type="text" value={b.desc} onChange={(e) => { const arr = [...bargainBadges]; arr[i] = {...arr[i], desc: e.target.value}; setBargainBadges(arr); }} className={inputClass} placeholder="Description" />
                      <button onClick={() => setBargainBadges(bargainBadges.filter((_, j) => j !== i))} className="text-red-400 hover:text-red-600 text-sm text-center">✕</button>
                    </div>
                  ))}
                  <button onClick={() => setBargainBadges([...bargainBadges, { threshold: 0, name: '', emoji: '🏅', desc: '' }])} className={addBtnClass}>+ Add Bargain Badge</button>

                  <p className={`${hdClass} text-xs mt-4`}>Collector Badges</p>
                  {collectorBadges.map((b, i) => (
                    <div key={i} className="grid grid-cols-5 gap-1 items-center">
                      <input type="number" value={b.threshold} onChange={(e) => { const arr = [...collectorBadges]; arr[i] = {...arr[i], threshold: parseInt(e.target.value) || 0}; setCollectorBadges(arr); }} className={inputClass} placeholder="Threshold" />
                      <input type="text" value={b.emoji} onChange={(e) => { const arr = [...collectorBadges]; arr[i] = {...arr[i], emoji: e.target.value}; setCollectorBadges(arr); }} className={inputClass} placeholder="Emoji" />
                      <input type="text" value={b.name} onChange={(e) => { const arr = [...collectorBadges]; arr[i] = {...arr[i], name: e.target.value}; setCollectorBadges(arr); }} className={inputClass} placeholder="Name" />
                      <input type="text" value={b.desc} onChange={(e) => { const arr = [...collectorBadges]; arr[i] = {...arr[i], desc: e.target.value}; setCollectorBadges(arr); }} className={inputClass} placeholder="Description" />
                      <button onClick={() => setCollectorBadges(collectorBadges.filter((_, j) => j !== i))} className="text-red-400 hover:text-red-600 text-sm text-center">✕</button>
                    </div>
                  ))}
                  <button onClick={() => setCollectorBadges([...collectorBadges, { threshold: 0, name: '', emoji: '🏅', desc: '' }])} className={addBtnClass}>+ Add Collector Badge</button>
                </div>
              </details>
            </>
          );
        })()}

        <button
          onClick={handleSave}
          className="w-full bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg font-bold text-sm"
        >
          Save Settings
        </button>
      </div>
    </div>
  );
};

// ============================================================
// Avatar Management Component
// ============================================================
const AvatarManagement = ({ avatars, API, adminToken, isDarkMode, showNotification, onRefresh }) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingAvatar, setEditingAvatar] = useState(null);
  const [form, setForm] = useState({ name: '', image_url: '', gender: 'neutral', is_default: false });
  const [uploading, setUploading] = useState(false);
  const fileInputRef = React.useRef(null);

  const resetForm = () => {
    setForm({ name: '', image_url: '', gender: 'neutral', is_default: false });
    setShowAddForm(false);
    setEditingAvatar(null);
  };

  const handleSubmit = async () => {
    if (!form.name || !form.image_url) {
      showNotification('Name and Image URL are required', 'error');
      return;
    }
    try {
      const url = editingAvatar
        ? `${API}/admin/souk-clothes/avatars/${editingAvatar.id}`
        : `${API}/admin/souk-clothes/avatars`;
      const res = await fetch(url, {
        method: editingAvatar ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
        body: JSON.stringify(form)
      });
      if (res.ok) {
        showNotification(editingAvatar ? 'Avatar updated!' : 'Avatar added!', 'success');
        resetForm();
        onRefresh();
      } else {
        showNotification('Failed to save avatar', 'error');
      }
    } catch (err) {
      showNotification('Error: ' + err.message, 'error');
    }
  };

  const handleDelete = async (avatarId) => {
    if (!window.confirm('Delete this avatar?')) return;
    try {
      const res = await fetch(`${API}/admin/souk-clothes/avatars/${avatarId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (res.ok) {
        showNotification('Avatar deleted', 'success');
        onRefresh();
      }
    } catch (err) {
      showNotification('Error: ' + err.message, 'error');
    }
  };

  const handleToggleActive = async (avatar) => {
    try {
      const res = await fetch(`${API}/admin/souk-clothes/avatars/${avatar.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
        body: JSON.stringify({ is_active: avatar.is_active === false })
      });
      if (res.ok) {
        showNotification(avatar.is_active === false ? 'Avatar shown!' : 'Avatar hidden!', 'success');
        onRefresh();
      }
    } catch (err) {
      showNotification('Error: ' + err.message, 'error');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      showNotification('Please select an image file (PNG, JPG, etc.)', 'error');
      return;
    }
    setUploading(true);
    try {
      // Convert to base64 data URL for now (works without external storage)
      const reader = new FileReader();
      reader.onload = (ev) => {
        setForm(prev => ({ ...prev, image_url: ev.target.result }));
        setUploading(false);
        showNotification('Image loaded! You can also paste a Dropbox/URL link instead.', 'success');
      };
      reader.onerror = () => {
        showNotification('Error reading file', 'error');
        setUploading(false);
      };
      reader.readAsDataURL(file);
    } catch (err) {
      showNotification('Upload error: ' + err.message, 'error');
      setUploading(false);
    }
  };

  const startEdit = (avatar) => {
    setEditingAvatar(avatar);
    setForm({
      name: avatar.name || '',
      image_url: avatar.image_url || '',
      gender: avatar.gender || 'neutral',
      is_default: avatar.is_default || false
    });
    setShowAddForm(true);
  };

  const cardBg = isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200';
  const textColor = isDarkMode ? 'text-white' : 'text-gray-800';
  const subText = isDarkMode ? 'text-gray-400' : 'text-gray-600';
  const inputBg = isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-800';

  return (
    <div className="space-y-4">
      <div className={`rounded-lg p-4 border ${cardBg}`}>
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className={`text-lg font-bold ${textColor}`}>Avatar Images</h3>
            <p className={`text-sm ${subText}`}>Upload avatar images for the Dress-Up feature. Users can choose which avatar to dress up.</p>
          </div>
          <button
            onClick={() => { resetForm(); setShowAddForm(true); }}
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg font-bold text-sm"
          >
            + Add Avatar
          </button>
        </div>

        {/* Add/Edit Form */}
        {showAddForm && (
          <div className={`rounded-lg p-4 border mb-4 ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-300'}`}>
            <h4 className={`font-bold mb-3 ${textColor}`}>{editingAvatar ? 'Edit Avatar' : 'Add New Avatar'}</h4>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className={`block text-sm font-medium mb-1 ${textColor}`}>Name *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g. Male Avatar, Female Hijab, etc."
                  className={`w-full px-3 py-2 rounded-lg border ${inputBg}`}
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1 ${textColor}`}>Gender</label>
                <select
                  value={form.gender}
                  onChange={(e) => setForm(prev => ({ ...prev, gender: e.target.value }))}
                  className={`w-full px-3 py-2 rounded-lg border ${inputBg}`}
                >
                  <option value="neutral">Neutral</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </select>
              </div>
            </div>

            <div className="mt-3">
              <label className={`block text-sm font-medium mb-1 ${textColor}`}>Image URL (Dropbox, direct link, etc.) *</label>
              <input
                type="url"
                value={form.image_url}
                onChange={(e) => setForm(prev => ({ ...prev, image_url: e.target.value }))}
                placeholder="https://dl.dropboxusercontent.com/... or any direct image URL"
                className={`w-full px-3 py-2 rounded-lg border ${inputBg}`}
              />
              <p className={`text-xs mt-1 ${subText}`}>
                Tip: For Dropbox, change "www.dropbox.com" to "dl.dropboxusercontent.com" and remove "?dl=0" for a direct link.
              </p>
            </div>

            <div className="mt-3">
              <label className={`block text-sm font-medium mb-1 ${textColor}`}>Or Upload Image</label>
              <div className="flex gap-2 items-center">
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-bold"
                  disabled={uploading}
                >
                  {uploading ? 'Loading...' : 'Choose File'}
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="hidden"
                />
                <span className={`text-xs ${subText}`}>PNG with transparent background recommended</span>
              </div>
            </div>

            {/* Preview */}
            {form.image_url && (
              <div className="mt-3">
                <label className={`block text-sm font-medium mb-1 ${textColor}`}>Preview</label>
                <div className="bg-gray-200 rounded-lg p-4 inline-block">
                  <img
                    src={form.image_url}
                    alt="Preview"
                    className="h-48 object-contain"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                </div>
              </div>
            )}

            <div className="mt-3 flex items-center gap-2">
              <input
                type="checkbox"
                id="is_default"
                checked={form.is_default}
                onChange={(e) => setForm(prev => ({ ...prev, is_default: e.target.checked }))}
              />
              <label htmlFor="is_default" className={`text-sm ${textColor}`}>Set as default avatar</label>
            </div>

            <div className="mt-4 flex gap-2">
              <button onClick={handleSubmit} className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg font-bold text-sm">
                {editingAvatar ? 'Update' : 'Save Avatar'}
              </button>
              <button onClick={resetForm} className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg font-bold text-sm">
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Avatar Grid */}
        {avatars.length === 0 ? (
          <div className={`text-center py-8 ${subText}`}>
            No avatars yet. Click "Add Avatar" to upload your first one.
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {avatars.map(avatar => (
              <div key={avatar.id} className={`rounded-lg border p-3 ${cardBg} relative ${avatar.is_active === false ? 'opacity-50' : ''}`}>
                {avatar.is_default && (
                  <span className="absolute top-1 right-1 bg-green-500 text-white text-[10px] px-2 py-0.5 rounded-full font-bold">Default</span>
                )}
                {avatar.is_active === false && (
                  <span className="absolute top-1 left-1 bg-red-500 text-white text-[10px] px-2 py-0.5 rounded-full font-bold">Hidden</span>
                )}
                <div className="bg-gray-200 rounded-lg p-2 mb-2 flex items-center justify-center h-40">
                  {avatar.image_url ? (
                    <img src={avatar.image_url} alt={avatar.name} className="h-full object-contain" />
                  ) : (
                    <span className="text-6xl">👤</span>
                  )}
                </div>
                <h4 className={`font-bold text-sm ${textColor}`}>{avatar.name}</h4>
                <p className={`text-xs ${subText}`}>{avatar.gender}</p>
                <div className="flex gap-1 mt-2">
                  <button onClick={() => handleToggleActive(avatar)} className={`${avatar.is_active === false ? 'bg-green-500 hover:bg-green-600' : 'bg-gray-500 hover:bg-gray-600'} text-white px-2 py-1 rounded text-xs font-bold flex-1`}>
                    {avatar.is_active === false ? 'Show' : 'Hide'}
                  </button>
                  <button onClick={() => startEdit(avatar)} className="bg-yellow-500 hover:bg-yellow-600 text-white px-2 py-1 rounded text-xs font-bold flex-1">Edit</button>
                  <button onClick={() => handleDelete(avatar.id)} className="bg-red-500 hover:bg-red-600 text-white px-2 py-1 rounded text-xs font-bold flex-1">Delete</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════
// Avatar Parts Manager — Dynamic categories for Paper Doll system
// ═══════════════════════════════════════════════════════════════════════
const AvatarPartsManager = ({ API, adminToken, isDarkMode, showNotification }) => {
  const [parts, setParts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ name: '', type: '', image_url: '', gender: 'neutral', sort_order: 0 });
  const [showAddCat, setShowAddCat] = useState(false);
  const [catForm, setCatForm] = useState({ name: '', key: '', icon: '', sort_order: 0 });
  const [editingCat, setEditingCat] = useState(null);
  const [editingPart, setEditingPart] = useState(null);

  const fetchParts = async () => {
    try {
      const res = await fetch(`${API}/admin/souk-clothes/avatar-parts`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (res.ok) setParts(await res.json());
    } catch (err) {
      console.error('Error fetching avatar parts:', err);
    }
    setLoading(false);
  };

  const fetchCategories = async () => {
    try {
      const res = await fetch(`${API}/admin/souk-clothes/avatar-categories`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (res.ok) {
        const cats = await res.json();
        setCategories(cats);
        if (cats.length > 0 && !addForm.type) {
          setAddForm(f => ({ ...f, type: cats[0].key }));
        }
      }
    } catch (err) {
      console.error('Error fetching avatar categories:', err);
    }
  };

  useEffect(() => { fetchParts(); fetchCategories(); }, []);

  const handleAddCat = async () => {
    if (!catForm.name || !catForm.key) {
      showNotification('Name and key are required', 'error');
      return;
    }
    try {
      const res = await fetch(`${API}/admin/souk-clothes/avatar-categories`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
        body: JSON.stringify(catForm)
      });
      if (res.ok) {
        showNotification(`Category "${catForm.name}" created!`, 'success');
        setShowAddCat(false);
        setCatForm({ name: '', key: '', icon: '', sort_order: 0 });
        fetchCategories();
      } else {
        const err = await res.json();
        showNotification(err.detail || 'Error creating category', 'error');
      }
    } catch (err) {
      showNotification('Error creating category', 'error');
    }
  };

  const handleToggleCat = async (cat) => {
    try {
      await fetch(`${API}/admin/souk-clothes/avatar-categories/${cat.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
        body: JSON.stringify({ is_active: !cat.is_active })
      });
      fetchCategories();
    } catch (err) {
      showNotification('Error updating category', 'error');
    }
  };

  const handleDeleteCat = async (catId) => {
    if (!window.confirm('Disable this category?')) return;
    try {
      await fetch(`${API}/admin/souk-clothes/avatar-categories/${catId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      showNotification('Category disabled', 'success');
      fetchCategories();
    } catch (err) {
      showNotification('Error deleting category', 'error');
    }
  };

  const handleSaveEditCat = async () => {
    if (!editingCat || !editingCat.name) return;
    try {
      const res = await fetch(`${API}/admin/souk-clothes/avatar-categories/${editingCat.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
        body: JSON.stringify({ name: editingCat.name, icon: editingCat.icon, sort_order: editingCat.sort_order })
      });
      if (res.ok) {
        showNotification('Category updated', 'success');
        setEditingCat(null);
        fetchCategories();
      } else {
        const err = await res.json();
        showNotification(err.detail || 'Error updating category', 'error');
      }
    } catch (err) {
      showNotification('Error updating category', 'error');
    }
  };

  const handleSaveEditPart = async () => {
    if (!editingPart) return;
    try {
      const res = await fetch(`${API}/admin/souk-clothes/avatar-parts/${editingPart.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
        body: JSON.stringify({ name: editingPart.name, image_url: editingPart.image_url, gender: editingPart.gender, sort_order: editingPart.sort_order, type: editingPart.type })
      });
      if (res.ok) {
        showNotification('Part updated', 'success');
        setEditingPart(null);
        fetchParts();
      } else {
        showNotification('Error updating part', 'error');
      }
    } catch (err) {
      showNotification('Error updating part', 'error');
    }
  };

  const handleAdd = async () => {
    if (!addForm.name || !addForm.image_url) {
      showNotification('Name and image URL are required', 'error');
      return;
    }
    if (!addForm.type) {
      showNotification('Please select a category (create one first if needed)', 'error');
      return;
    }
    try {
      const res = await fetch(`${API}/admin/souk-clothes/avatar-parts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
        body: JSON.stringify(addForm)
      });
      if (res.ok) {
        const catName = categories.find(c => c.key === addForm.type)?.name || addForm.type;
        showNotification(`${catName} part added!`, 'success');
        setShowAdd(false);
        setAddForm(f => ({ ...f, name: '', image_url: '', gender: 'neutral', sort_order: 0 }));
        fetchParts();
      }
    } catch (err) {
      showNotification('Error adding part', 'error');
    }
  };

  const handleToggle = async (part) => {
    try {
      await fetch(`${API}/admin/souk-clothes/avatar-parts/${part.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
        body: JSON.stringify({ is_active: !part.is_active })
      });
      fetchParts();
    } catch (err) {
      showNotification('Error updating part', 'error');
    }
  };

  const handleDelete = async (partId) => {
    if (!window.confirm('Disable this avatar part?')) return;
    try {
      await fetch(`${API}/admin/souk-clothes/avatar-parts/${partId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      showNotification('Part disabled', 'success');
      fetchParts();
    } catch (err) {
      showNotification('Error deleting part', 'error');
    }
  };

  const inputCls = `w-full mt-1 px-3 py-2 rounded-lg border text-sm ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`;
  const labelCls = `text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`;

  return (
    <div className={`mt-6 rounded-lg p-5 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border`}>
      <h3 className={`text-lg font-bold mb-1 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
        🧩 Avatar Parts (Paper Doll)
      </h3>
      <p className={`text-xs mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
        Create categories first, then upload transparent PNG parts for each category
      </p>

      {/* ═══ Categories Management ═══ */}
      <div className={`mb-5 p-4 rounded-lg ${isDarkMode ? 'bg-gray-700/50' : 'bg-blue-50'} border ${isDarkMode ? 'border-gray-600' : 'border-blue-200'}`}>
        <div className="flex justify-between items-center mb-3">
          <h4 className={`text-sm font-bold ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>
            📂 Part Categories
          </h4>
          <button
            onClick={() => setShowAddCat(!showAddCat)}
            className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-medium"
          >
            + New Category
          </button>
        </div>

        {/* Add Category Form */}
        {showAddCat && (
          <div className={`mb-3 p-3 rounded-lg ${isDarkMode ? 'bg-gray-600' : 'bg-white'} border ${isDarkMode ? 'border-gray-500' : 'border-blue-200'}`}>
            <div className="grid grid-cols-4 gap-2 mb-2">
              <div>
                <label className={labelCls}>Name</label>
                <input
                  value={catForm.name}
                  onChange={e => {
                    const name = e.target.value;
                    setCatForm(f => ({ ...f, name, key: f.key || name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '') }));
                  }}
                  placeholder="e.g. Hair Styles"
                  className={inputCls}
                />
              </div>
              <div>
                <label className={labelCls}>Key (unique)</label>
                <input
                  value={catForm.key}
                  onChange={e => setCatForm(f => ({ ...f, key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') }))}
                  placeholder="e.g. hair"
                  className={inputCls}
                />
              </div>
              <div>
                <label className={labelCls}>Icon (emoji)</label>
                <input
                  value={catForm.icon}
                  onChange={e => setCatForm(f => ({ ...f, icon: e.target.value }))}
                  placeholder="e.g. 💇"
                  className={inputCls}
                />
              </div>
              <div>
                <label className={labelCls}>Order</label>
                <input
                  type="number"
                  value={catForm.sort_order}
                  onChange={e => setCatForm(f => ({ ...f, sort_order: parseInt(e.target.value) || 0 }))}
                  className={inputCls}
                />
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={handleAddCat} className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs font-medium">Create</button>
              <button onClick={() => setShowAddCat(false)} className="px-3 py-1.5 bg-gray-400 hover:bg-gray-500 text-white rounded-lg text-xs font-medium">Cancel</button>
            </div>
          </div>
        )}

        {/* Category List */}
        {categories.length === 0 ? (
          <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            No categories yet. Create "Body", "Head", "Eyes", etc. to get started.
          </p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {categories.map(cat => (
              editingCat?.id === cat.id ? (
                <div key={cat.id} className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg border text-sm ${isDarkMode ? 'border-yellow-500 bg-yellow-900/30' : 'border-yellow-400 bg-yellow-50'}`}>
                  <input value={editingCat.icon} onChange={e => setEditingCat(p => ({...p, icon: e.target.value}))} className={`w-8 px-1 py-0.5 text-center rounded border text-sm ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`} placeholder="🧩" />
                  <input value={editingCat.name} onChange={e => setEditingCat(p => ({...p, name: e.target.value}))} className={`w-24 px-1.5 py-0.5 rounded border text-sm ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`} placeholder="Name" />
                  <input type="number" value={editingCat.sort_order} onChange={e => setEditingCat(p => ({...p, sort_order: parseInt(e.target.value) || 0}))} className={`w-12 px-1 py-0.5 rounded border text-sm text-center ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`} />
                  <button onClick={handleSaveEditCat} className="text-[10px] px-2 py-0.5 rounded bg-green-500 text-white font-medium">Save</button>
                  <button onClick={() => setEditingCat(null)} className="text-[10px] px-1.5 py-0.5 rounded bg-gray-400 text-white">Cancel</button>
                </div>
              ) : (
                <div key={cat.id} className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm ${
                  cat.is_active !== false
                    ? (isDarkMode ? 'border-blue-500 bg-blue-900/30 text-blue-300' : 'border-blue-300 bg-blue-100 text-blue-800')
                    : 'opacity-40 border-gray-400 bg-gray-200 text-gray-600'
                }`}>
                  <span>{cat.icon || '🧩'}</span>
                  <span className="font-medium">{cat.name}</span>
                  <span className={`text-[10px] ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>({cat.key})</span>
                  <span className={`text-[10px] ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>#{cat.sort_order}</span>
                  <button onClick={() => setEditingCat({ id: cat.id, name: cat.name, icon: cat.icon || '', sort_order: cat.sort_order || 0 })} className="text-[10px] px-1.5 py-0.5 rounded bg-blue-200 text-blue-700">Edit</button>
                  <button onClick={() => handleToggleCat(cat)} className={`text-[10px] px-1.5 py-0.5 rounded ${cat.is_active !== false ? 'bg-green-200 text-green-700' : 'bg-gray-300 text-gray-600'}`}>
                    {cat.is_active !== false ? 'On' : 'Off'}
                  </button>
                </div>
              )
            ))}
          </div>
        )}
      </div>

      {/* ═══ Add Part Button + Form ═══ */}
      <div className="flex justify-between items-center mb-4">
        <h4 className={`text-sm font-bold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
          🖼️ Parts ({parts.length} total)
        </h4>
        <button
          onClick={() => setShowAdd(!showAdd)}
          className={`px-3 py-1.5 text-white rounded-lg text-sm font-medium ${categories.length === 0 ? 'bg-gray-400 cursor-not-allowed' : 'bg-purple-600 hover:bg-purple-700'}`}
          disabled={categories.length === 0}
          title={categories.length === 0 ? 'Create a category first' : 'Add a new part'}
        >
          + Add Part
        </button>
      </div>

      {showAdd && categories.length > 0 && (
        <div className={`mb-4 p-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'} border ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <label className={labelCls}>Name</label>
              <input
                value={addForm.name}
                onChange={e => setAddForm(f => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Long braids, Slim body"
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>Categories (pick 1 or more)</label>
              <div className={`mt-1 flex flex-wrap gap-1.5 px-2 py-2 rounded-lg border ${isDarkMode ? 'bg-gray-600 border-gray-500' : 'bg-white border-gray-300'}`}>
                {categories.filter(c => c.is_active !== false).map(cat => {
                  const types = (addForm.type || '').split(',').filter(Boolean);
                  const checked = types.includes(cat.key);
                  return (
                    <label key={cat.key} className={`flex items-center gap-1 text-xs px-2 py-1 rounded cursor-pointer ${checked ? (isDarkMode ? 'bg-blue-700 text-white' : 'bg-blue-100 text-blue-800 font-medium') : (isDarkMode ? 'text-gray-400 hover:bg-gray-500' : 'text-gray-500 hover:bg-gray-100')}`}>
                      <input type="checkbox" checked={checked} onChange={() => {
                        const next = checked ? types.filter(t => t !== cat.key) : [...types, cat.key];
                        setAddForm(f => ({ ...f, type: next.join(',') }));
                      }} className="w-3 h-3" />
                      {cat.icon || '🧩'} {cat.name}
                    </label>
                  );
                })}
              </div>
            </div>
          </div>
          <div className="mb-3">
            <label className={labelCls}>Image URL (transparent PNG)</label>
            <input
              value={addForm.image_url}
              onChange={e => setAddForm(f => ({ ...f, image_url: e.target.value }))}
              placeholder="https://..."
              className={inputCls}
            />
          </div>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <label className={labelCls}>Gender</label>
              <select value={addForm.gender} onChange={e => setAddForm(f => ({ ...f, gender: e.target.value }))} className={inputCls}>
                <option value="neutral">Neutral</option>
                <option value="female">Female</option>
                <option value="male">Male</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Sort Order</label>
              <input
                type="number"
                value={addForm.sort_order}
                onChange={e => setAddForm(f => ({ ...f, sort_order: parseInt(e.target.value) || 0 }))}
                className={inputCls}
              />
            </div>
          </div>
          {addForm.image_url && (
            <div className="mb-3 bg-gray-100 rounded-lg p-2 h-24 flex items-center justify-center">
              <img src={addForm.image_url} alt="Preview" className="h-full object-contain" />
            </div>
          )}
          <div className="flex gap-2">
            <button onClick={handleAdd} className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium">Save</button>
            <button onClick={() => setShowAdd(false)} className="px-4 py-2 bg-gray-400 hover:bg-gray-500 text-white rounded-lg text-sm font-medium">Cancel</button>
          </div>
        </div>
      )}

      {/* ═══ Parts grouped by category ═══ */}
      {loading ? (
        <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Loading...</p>
      ) : categories.length === 0 ? (
        <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          Create categories above first (e.g. Body, Head, Eyes, Hair), then add parts to each.
        </p>
      ) : (
        <>
          {categories.map(cat => {
            const catParts = parts.filter(p => (p.type || '').split(',').includes(cat.key));
            return (
              <div key={cat.key} className="mb-4">
                <h4 className={`text-sm font-bold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  {cat.icon || '🧩'} {cat.name} ({catParts.length})
                  {cat.is_active === false && <span className="ml-2 text-[10px] text-red-400">(disabled)</span>}
                </h4>
                {catParts.length === 0 ? (
                  <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    No {cat.name.toLowerCase()} parts uploaded yet
                  </p>
                ) : (
                  <div className="space-y-3">
                    {[
                      { key: 'female', label: '♀ Female', border: 'border-pink-400', bg: isDarkMode ? 'bg-pink-900/20' : 'bg-pink-50', text: 'text-pink-500' },
                      { key: 'male', label: '♂ Male', border: 'border-blue-400', bg: isDarkMode ? 'bg-blue-900/20' : 'bg-blue-50', text: 'text-blue-500' },
                      { key: 'neutral', label: '⚪ Neutral', border: 'border-gray-400', bg: isDarkMode ? 'bg-gray-700/50' : 'bg-gray-50', text: isDarkMode ? 'text-gray-400' : 'text-gray-500' }
                    ].map(g => {
                      const gParts = catParts.filter(p => (p.gender || 'neutral') === g.key);
                      if (gParts.length === 0) return null;
                      return (
                        <div key={g.key} className={`rounded-lg border ${g.border} ${g.bg} p-3`}>
                          <p className={`text-xs font-bold mb-2 ${g.text}`}>{g.label} ({gParts.length})</p>
                          <div className="grid grid-cols-4 sm:grid-cols-5 gap-2">
                            {gParts.map(part => (
                              editingPart?.id === part.id ? (
                                <div key={part.id} className={`rounded-lg border p-2 col-span-2 ${isDarkMode ? 'border-yellow-500 bg-gray-700' : 'border-yellow-400 bg-yellow-50'}`}>
                                  <div className="space-y-1.5">
                                    <input value={editingPart.name} onChange={e => setEditingPart(p => ({...p, name: e.target.value}))} className={`w-full px-2 py-1 rounded border text-xs ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`} placeholder="Name" />
                                    <input value={editingPart.image_url} onChange={e => setEditingPart(p => ({...p, image_url: e.target.value}))} className={`w-full px-2 py-1 rounded border text-xs ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`} placeholder="Image URL" />
                                    <div className="flex gap-1.5">
                                      <select value={editingPart.gender} onChange={e => setEditingPart(p => ({...p, gender: e.target.value}))} className={`flex-1 px-1.5 py-1 rounded border text-xs ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}>
                                        <option value="female">♀ Female</option>
                                        <option value="male">♂ Male</option>
                                        <option value="neutral">Neutral</option>
                                      </select>
                                      <div className={`flex-1 flex flex-wrap gap-1 px-1.5 py-1 rounded border ${isDarkMode ? 'border-gray-500' : 'border-gray-300'}`}>
                                        {categories.filter(c => c.is_active !== false).map(c => {
                                          const types = (editingPart.type || '').split(',').filter(Boolean);
                                          const checked = types.includes(c.key);
                                          return (
                                            <label key={c.key} className={`flex items-center gap-0.5 text-[10px] px-1 py-0.5 rounded cursor-pointer ${checked ? (isDarkMode ? 'bg-blue-700 text-white' : 'bg-blue-100 text-blue-800') : (isDarkMode ? 'text-gray-400' : 'text-gray-500')}`}>
                                              <input type="checkbox" checked={checked} onChange={() => {
                                                const next = checked ? types.filter(t => t !== c.key) : [...types, c.key];
                                                setEditingPart(p => ({...p, type: next.join(',')}));
                                              }} className="w-3 h-3" />
                                              {c.icon} {c.name}
                                            </label>
                                          );
                                        })}
                                      </div>
                                    </div>
                                    {editingPart.image_url && (
                                      <div className="h-14 flex items-center justify-center bg-gray-100 rounded">
                                        <img src={editingPart.image_url} alt="Preview" className="h-full object-contain" />
                                      </div>
                                    )}
                                    <div className="flex gap-1">
                                      <button onClick={handleSaveEditPart} className="flex-1 text-[10px] px-2 py-1 rounded bg-green-500 text-white font-medium">Save</button>
                                      <button onClick={() => setEditingPart(null)} className="flex-1 text-[10px] px-2 py-1 rounded bg-gray-400 text-white">Cancel</button>
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                <div key={part.id} className={`rounded-lg border p-1.5 relative ${part.is_active === false ? 'opacity-40' : ''} ${isDarkMode ? 'border-gray-600 bg-gray-700' : 'border-gray-200 bg-white'}`}>
                                  <div className="h-16 flex items-center justify-center mb-1">
                                    {part.image_url ? (
                                      <img src={part.image_url} alt={part.name} className="h-full object-contain" />
                                    ) : (
                                      <span className="text-2xl">{cat.icon || '🧩'}</span>
                                    )}
                                  </div>
                                  <p className={`text-[10px] font-medium text-center truncate ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{part.name}</p>
                                  <div className="flex gap-1 mt-1 justify-center">
                                    <button onClick={() => setEditingPart({ id: part.id, name: part.name, image_url: part.image_url || '', gender: part.gender || 'neutral', sort_order: part.sort_order || 0, type: part.type })} className="text-[9px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">
                                      Edit
                                    </button>
                                    <button onClick={() => handleToggle(part)} className={`text-[9px] px-1.5 py-0.5 rounded ${part.is_active !== false ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'}`}>
                                      {part.is_active !== false ? 'On' : 'Off'}
                                    </button>
                                    <button onClick={() => handleDelete(part.id)} className="text-[9px] px-1.5 py-0.5 rounded bg-red-100 text-red-700">
                                      Del
                                    </button>
                                  </div>
                                </div>
                              )
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
          {/* Uncategorized parts (parts with types that don't match any category) */}
          {(() => {
            const catKeys = new Set(categories.map(c => c.key));
            const uncategorized = parts.filter(p => !(p.type || '').split(',').some(t => catKeys.has(t)));
            if (uncategorized.length === 0) return null;
            return (
              <div className="mb-4">
                <h4 className={`text-sm font-bold mb-2 text-orange-500`}>
                  ⚠️ Uncategorized ({uncategorized.length})
                </h4>
                <p className={`text-xs mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  These parts have types that don't match any category. Create the matching category or update the part type.
                </p>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                  {uncategorized.map(part => (
                    <div key={part.id} className={`rounded-lg border p-2 ${isDarkMode ? 'border-orange-600 bg-gray-700' : 'border-orange-200 bg-orange-50'}`}>
                      <div className="h-20 flex items-center justify-center mb-1">
                        {part.image_url ? (
                          <img src={part.image_url} alt={part.name} className="h-full object-contain" />
                        ) : (
                          <span className="text-3xl">❓</span>
                        )}
                      </div>
                      <p className={`text-xs font-medium text-center truncate ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{part.name}</p>
                      <p className={`text-[10px] text-center text-orange-500`}>type: {part.type}</p>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
        </>
      )}
    </div>
  );
};

export default SoukClothesAdmin;